// frontend/src/services/socket.js
import { io } from "socket.io-client";
import { getAuthToken } from '../utils/auth';
import { canonicalZodiacRoomId } from '../utils/zodiacRoomIdentity';
import { presenceDiag } from '../utils/presenceDiag';
import { SOCKET_IO_ORIGIN } from '../platform/endpoints';

const SOCKET_DEV_DIAGNOSTICS = process.env.NODE_ENV === 'development';
const SOCKET_TRACE_STARTED_AT = typeof performance !== 'undefined' ? performance.now() : Date.now();

function debugSocketsEnabled() {
  if (process.env.NODE_ENV === 'production') return false;
  if (SOCKET_DEV_DIAGNOSTICS) return true;
  try { return localStorage.getItem('debugSockets') === '1'; } catch { return false; }
}

function logSocketDiagnostic(event, details) {
  if (!debugSocketsEnabled()) return;
  try {
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    console.info('[socket-debug]', {
      timestamp: new Date().toISOString(),
      elapsedMs: Math.round((now - SOCKET_TRACE_STARTED_AT) * 10) / 10,
      event,
      ...details,
    });
  } catch {}
}

function socketAuthVersion(target) {
  return Number(target?.__z10_auth_version || 0);
}

function logSocketConnectAttempt(token, source = 'unspecified') {
  const safeToken = typeof token === 'string' ? token : '';
  logSocketDiagnostic('socket:connect_attempt', {
    hasToken: Boolean(safeToken),
    authenticatedUserId: getLocalUserId() || null,
    socketConnected: Boolean(socket?.connected),
    connectingTokenPresent: Boolean(socket?.__z10_connectingAuthToken),
    connectedTokenPresent: Boolean(socket?.__z10_connectedAuthToken),
    source,
    tabId: TAB_ID,
    authDisabled: Boolean(socket?.__z10_auth_disabled),
    authGeneration: Number(socket?.__z10_auth_generation || 0),
    authVersion: socketAuthVersion(socket),
  });
}

// Rate-limit for sessionInvalidated logs (module scoped)
let __z10_lastSessionInvalidationLog = 0;

// Read JWT token from localStorage (or other keys as fallback)


// Stable per-tab id (barrera anti-duplicado por pestaña/dispositivo)
function getOrCreateTabId() {
  try {
    const k = "z10_tab_id";
    const existing = sessionStorage.getItem(k);
    if (existing && String(existing).trim()) return String(existing);
    const v =
      (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) ||
      Math.random().toString(36).slice(2) + Date.now().toString(36);
    try { sessionStorage.setItem(k, v); } catch {}
    return v;
  } catch {
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
}

// Keep socket.auth.token and query.tabId in sync even within the SAME tab (storage event doesn't fire in same tab)
function syncSocketAuth() {
  try {
    if (!socket) return false;
    if (socket.__z10_auth_disabled) {
      socket.auth = { tabId: TAB_ID };
      socket.__z10_connectingAuthToken = null;
      socket.__z10_connectedAuthToken = null;
      return false;
    }
    const t = getAuthToken() || null;
    const previousPendingToken = socket.__z10_connectingAuthToken || socket.auth?.token || null;
    const tokenChanged = previousPendingToken !== t;
    logSocketDiagnostic('auth:token_read', {
      socketId: socket.id || socket.__z10_lastSocketId || null,
      socketConnected: Boolean(socket.connected),
      authenticatedUserId: getLocalUserId() || null,
      tokenPresent: Boolean(t),
      tokenLength: typeof t === 'string' ? t.length : 0,
      tokenChanged,
      authVersion: socketAuthVersion(socket),
    });

    // socket.auth is the next handshake payload. It does not prove which
    // token authenticated the already-connected transport.
    socket.auth = {
      ...(socket.auth || {}),
      tabId: TAB_ID,
      ...(t ? { token: t } : {}),
    };
    if (!t) delete socket.auth.token;
    socket.__z10_connectingAuthToken = t;
    logSocketDiagnostic('auth:socket_auth_updated', {
      socketId: socket.id || socket.__z10_lastSocketId || null,
      socketConnected: Boolean(socket.connected),
      authenticatedUserId: getLocalUserId() || null,
      tokenPresent: Boolean(t),
      tokenChanged,
      authVersion: socketAuthVersion(socket),
    });
    presenceDiag('socket:sync-auth', { socket, source: 'syncSocketAuth', extra: { tokenChanged: Boolean(t && t !== (socket.__z10_connectedAuthToken || null)) } });

    // Also mirror tabId into query params (some servers read socket.handshake.query)
    try {
      socket.io = socket.io || {};
      socket.io.opts = socket.io.opts || {};
      socket.io.opts.query = socket.io.opts.query || {};
      socket.io.opts.query.tabId = TAB_ID;
    } catch {}
    // A changed JWT is only a future-handshake credential. The already
    // connected namespace remains authenticated as its original verified
    // session and must not be treated as stale solely because the strings
    // differ.
    return tokenChanged;
  } catch {}
  return false;
}

// Refreshing an access token updates the next handshake without interrupting
// an already-authenticated live connection.
export function updateSocketAuthenticationToken() {
  return syncSocketAuth();
}

export function enableSocketAuthentication({ token, userId, authGeneration, source = 'unspecified' } = {}) {
  try {
    const committedToken = String(token || '').trim();
    const committedUserId = String(userId || '').trim();
    const generation = Number(authGeneration);
    if (!committedToken || !committedUserId || !Number.isFinite(generation)) return false;
    if (getAuthToken() !== committedToken) return false;
    const previousGeneration = Number(socket.__z10_auth_generation || -1);
    if (generation < previousGeneration || (socket.__z10_auth_disabled && generation <= previousGeneration)) return false;
    const sameCommittedSession = !socket.__z10_auth_disabled &&
      generation === previousGeneration &&
      socket.__z10_auth_committed_token === committedToken &&
      socket.__z10_auth_committed_user_id === committedUserId;
    logSocketDiagnostic('socket:auth_enable_requested', {
      source,
      socketId: socket.id || socket.__z10_lastSocketId || null,
      socketConnected: Boolean(socket.connected),
      socketActive: Boolean(socket.active),
      authDisabled: Boolean(socket.__z10_auth_disabled),
      authGeneration: previousGeneration,
      authVersion: socketAuthVersion(socket),
      authenticatedUserId: committedUserId,
      tabId: TAB_ID,
      noOp: sameCommittedSession,
    });
    // AuthContext can run both bootstrap and profile effects for one committed
    // session. Reapplying the exact tuple must not mutate handshake state or
    // provoke a replacement transport.
    if (sameCommittedSession) {
      if (!socket.connected) lastPresenceActivitySignal = null;
      return false;
    }
    socket.__z10_auth_disabled = false;
    socket.__z10_auth_generation = generation;
    socket.__z10_auth_committed_token = committedToken;
    socket.__z10_auth_committed_user_id = committedUserId;
    lastPresenceActivitySignal = null;
    socket.io.opts.reconnection = true;
    return syncSocketAuth();
  } catch { return false; }
}

const SOCKET_URL = SOCKET_IO_ORIGIN;

// === Estado local por pestaña (autoridad = sessionStorage) ===
let lastProfileSnapshot = {};
const joinedRooms = new Set();
let desiredSubscriptionVersion = 0;

// Track last successful identify (ms desde epoch) para no spamear
// IMPORTANT: store on the socket instance so it survives dev HMR/module reloads.
function __getIdentifyState() {
  try {
    if (!socket) return { lastAt: 0, lastSocketId: null, lastUserId: null, inflightBySid: {} };
    if (!socket.__z10_identify_state) {
      socket.__z10_identify_state = {
        lastAt: 0,
        lastSocketId: null,
        lastUserId: null,
        inflightBySid: {},
        softLockUntil: 0,
      };
    }
    return socket.__z10_identify_state;
  } catch {
    return { lastAt: 0, lastSocketId: null, lastUserId: null, inflightBySid: {}, softLockUntil: 0 };
  }
}

function maybeIdentify(userId, { force = false, source = 'unspecified' } = {}) {
  try {
    const uid = userId ? String(userId) : '';
    if (!uid || !socket) return false;
    if (!socket.connected) return false;

    const st = __getIdentifyState();
    const sid = String(socket.id || '');
    const now = Date.now();

    // Soft lock to avoid bursts caused by rapid UI actions / effects
    if (!force && st.softLockUntil && now < st.softLockUntil) return false;

    // Same socket + same user within 10s => skip
    if (!force && sid && st.lastSocketId === sid && st.lastUserId === uid && (now - (st.lastAt || 0)) < 10000) {
      return false;
    }

    // If an identify ACK is already in-flight for this socket, don't start another one.
    if (!force && sid && st.inflightBySid && st.inflightBySid[sid]) return false;

    logSocketDiagnostic('identify:emit', {
      source,
      requestedUserId: uid,
      socketId: socket.id || null,
      socketConnected: Boolean(socket.connected),
      authVersion: socketAuthVersion(socket),
      acknowledgementRequested: false,
    });
    socket.emit('identify', { userId: uid });
    logSocketDiagnostic('identify:no_ack_protocol', {
      source,
      requestedUserId: uid,
      socketId: socket.id || null,
      authVersion: socketAuthVersion(socket),
      detail: 'this client emission does not supply an acknowledgement callback',
    });

    st.lastAt = now;
    st.lastSocketId = sid || st.lastSocketId;
    st.lastUserId = uid;
    // short soft-lock window (prevents immediate repeats from cascaded calls)
    st.softLockUntil = now + 600;

    try { sessionStorage.setItem('userIdSnapshot', uid); } catch {}
    return true;
  } catch {
    return false;
  }
}

// Awaitable identify (ACK) for strict ordering before joinRoom / setProfile
async function ensureIdentify(userId, { force = false } = {}) {
  const uid = userId ? String(userId) : '';
  if (!uid || !socket || !socket.connected) return false;

  const st = __getIdentifyState();
  const sid = String(socket.id || '');
  const now = Date.now();

  // Same socket + same user within 10s => skip unless forced
  if (!force && sid && st.lastSocketId === sid && st.lastUserId === uid && (now - (st.lastAt || 0)) < 10000) {
    return false;
  }

  // Reuse in-flight promise if exists
  if (!force && sid && st.inflightBySid && st.inflightBySid[sid]) {
    try { return await st.inflightBySid[sid]; } catch { return false; }
  }

  const p = (async () => {
    try {
      logSocketDiagnostic('identify:emit', {
        source: 'ensureIdentify',
        requestedUserId: uid,
        socketId: socket.id || null,
        socketConnected: Boolean(socket.connected),
        authVersion: socketAuthVersion(socket),
        acknowledgementRequested: true,
      });
      presenceDiag('socket:identify:emit', { socket, source: 'ensureIdentify', userId: uid });
      const acknowledgement = await emitAck('identify', { userId: uid });
      logSocketDiagnostic('identify:ack', {
        source: 'ensureIdentify',
        requestedUserId: uid,
        acknowledgedUserId: acknowledgement?.userId || null,
        acknowledgementOk: acknowledgement?.ok !== false,
        socketId: socket.id || null,
        socketConnected: Boolean(socket.connected),
        authVersion: socketAuthVersion(socket),
      });
      const t = Date.now();
      st.lastAt = t;
      st.lastSocketId = String(socket.id || sid);
      st.lastUserId = uid;
      st.softLockUntil = t + 600;
      try { sessionStorage.setItem('userIdSnapshot', uid); } catch {}
      return true;
    } catch (error) {
      logSocketDiagnostic('identify:ack_error', {
        source: 'ensureIdentify',
        requestedUserId: uid,
        error: error?.message || String(error || ''),
        socketId: socket?.id || socket?.__z10_lastSocketId || null,
        socketConnected: Boolean(socket?.connected),
        authVersion: socketAuthVersion(socket),
      });
      return false;
    } finally {
      try { if (sid && st.inflightBySid) delete st.inflightBySid[sid]; } catch {}
    }
  })();

  if (sid) {
    st.inflightBySid = st.inflightBySid || {};
    st.inflightBySid[sid] = p;
  }
  return await p;
}

// Internal: keep a per-room debounce for auto-repair
const lastRepairByRoom = new Map();

// Join dedupe/throttle
const joinInflight = new Map();   // room -> Promise in-flight
const lastJoinAt = new Map();     // room -> timestamp ms
const refreshTimers = new Map();  // room -> timeout id


const messageCallbacks = new Set();
const likesUpdateCallbacks = new Set();
const conversationPresenceCallbacks = new Set();
const conversationPresenceSnapshotCallbacks = new Set();
const contactRequestReceivedCallbacks = new Set();
const contactAcceptedCallbacks = new Set();
const contactDeclinedCallbacks = new Set();
const contactRemovedCallbacks = new Set();
const presenceUpdateCallbacks = new Set();
const userPresenceCallbacks = new Set();
const zodiacRoomCountCallbacks = new Set();

// Fan-out for user-provided roomUsers callbacks (so our internal repair listener is never removed)
const roomUsersCallbacks = new Set();
function dispatchRoomUsers(payload) {
  for (const cb of roomUsersCallbacks) {
    try { cb(payload); } catch {}
  }
}

function dispatchZodiacRoomCounts(payload) {
  for (const cb of zodiacRoomCountCallbacks) {
    try { cb(payload); } catch {}
  }
}

// Add below the existing roomUsers callbacks
const userJoinedCallbacks = new Set();
const userLeftCallbacks = new Set();

function dispatchUserJoined(payload) {
  for (const cb of userJoinedCallbacks) {
    try { cb(payload); } catch {}
  }
}
function dispatchUserLeft(payload) {
  for (const cb of userLeftCallbacks) {
    try { cb(payload); } catch {}
  }
}

// --- Badges y previews de conversaciones (para notificaciones en Contacts) ---
// Fan-out para mensajes 1 a 1 (evita sobrescribir listeners)
const newConvMsgCallbacks = new Set();
function dispatchNewConversationMessage(payload) {
  for (const cb of newConvMsgCallbacks) {
    try { cb(payload); } catch {}
  }
}

const badgeUpdateCallbacks = new Set();
const convPreviewCallbacks = new Set();

function dispatchBadgeUpdate(payload) {
  for (const cb of badgeUpdateCallbacks) {
    try { cb(payload); } catch {}
  }
}
function dispatchConversationPreview(payload) {
  for (const cb of convPreviewCallbacks) {
    try { cb(payload); } catch {}
  }
}

function dispatchMessage(payload) {
  for (const cb of messageCallbacks) {
    try { cb(payload); } catch {}
  }
}
function dispatchLikesUpdate(payload) {
  for (const cb of likesUpdateCallbacks) {
    try { cb(payload); } catch {}
  }
}
function dispatchConversationPresence(payload) {
  for (const cb of conversationPresenceCallbacks) {
    try { cb(payload); } catch {}
  }
}
function dispatchConversationPresenceSnapshot(payload) {
  for (const cb of conversationPresenceSnapshotCallbacks) {
    try { cb(payload); } catch {}
  }
}
function dispatchContactRequestReceived(payload) {
  for (const cb of contactRequestReceivedCallbacks) {
    try { cb(payload); } catch {}
  }
}
function dispatchContactAccepted(payload) {
  for (const cb of contactAcceptedCallbacks) {
    try { cb(payload); } catch {}
  }
}
function dispatchContactDeclined(payload) {
  for (const cb of contactDeclinedCallbacks) {
    try { cb(payload); } catch {}
  }
}
function dispatchContactRemoved(payload) {
  for (const cb of contactRemovedCallbacks) {
    try { cb(payload); } catch {}
  }
}
function dispatchPresenceUpdate(payload) {
  for (const cb of presenceUpdateCallbacks) {
    try { cb(payload); } catch {}
  }
}
function dispatchUserPresence(payload) {
  for (const cb of userPresenceCallbacks) {
    try { cb(payload); } catch {}
  }
}

// --- Foro: notificaciones realtime ---
const forumNotifCallbacks = new Set();
function dispatchForumNotification(payload) {
  for (const cb of forumNotifCallbacks) {
    try { cb(payload); } catch {}
  }
  // También disparar un evento global por si la UI quiere escuchar sin importar imports
  try {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('forum:notification', { detail: payload || {} }));
    }
  } catch {}
}

// --- Notificaciones unificadas (foro + mensajes) ---
// Fan-out para que distintas UIs (Home/Contacts/etc.) puedan reaccionar sin pisarse listeners
const unifiedNotifCallbacks = new Set();
function dispatchUnifiedNotification(payload) {
  for (const cb of unifiedNotifCallbacks) {
    try { cb(payload); } catch {}
  }
  // Evento global opcional (útil para pantallas que no importan socket.js directamente)
  try {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('unified:notification', { detail: payload || {} }));
    }
  } catch {}
}

// Per-room dedupe windows for userJoined/userLeft (avoid burst & duplicates)
const lastJoinedSeen = new Map(); // room -> Map(socketId -> ts)
const lastLeftSeen = new Map();   // room -> Map(socketId -> ts)

function seenRecently(map, room, socketId, windowMs = 800) {
  const m = map.get(room) || new Map();
  const now = Date.now();

  // limpiar entradas viejas para evitar crecimiento infinito
  for (const [sid, ts] of m.entries()) {
    if (now - ts > windowMs) m.delete(sid);
  }

  const last = m.get(socketId) || 0;
  if (now - last < windowMs) return true;
  m.set(socketId, now);
  map.set(room, m);
  return false;
}

// Conversación activa en esta pestaña (para marcar como leído automáticamente)
let currentConversationId = null;
function isDocumentVisible() {
  try { return typeof document !== 'undefined' ? document.visibilityState === 'visible' : true; } catch { return true; }
}

// --- utils

function pickDefined(obj = {}) {
  const out = {};
  Object.keys(obj).forEach((k) => {
    const v = obj[k];
    if (v === undefined || v === null) return;
    if (typeof v === "string" && v.trim() === "") return;
    out[k] = v;
  });
  return out;
}

// Dedup robusto: colapsa por userId; si hay filas anónimas con el mismo perfil que una identificada, descártalas
function dedupeUsers(list) {
  const arr = Array.isArray(list) ? list : [];

  // helper para hash de perfil visible
  const norm = (s) => (s == null ? '' : String(s).trim().toLowerCase());
  const profHash = (u) =>
    [norm(u.fullName || 'Anónimo'), norm(u.zodiacSign || ''), norm(u.photoPath || ''), String(u.gender ?? '')].join('|');

  // 1) Group authenticated participants by canonical user id. Guest rows do
  // not receive a socket-id fallback because socket ids must never become UI
  // profile identities.
  const groups = new Map(); // key -> array de filas
  for (const u of arr) {
    const key = u.userId ? `u:${String(u.userId)}` : `n:${norm(u.fullName || 'Anónimo')}`;
    const g = groups.get(key) || [];
    g.push(u);
    groups.set(key, g);
  }

  // 2) En cada grupo, elegir la fila "mejor" (con más datos visibles). En empate, nos quedamos con la última.
  const bestByKey = new Map();
  for (const [k, g] of groups.entries()) {
    let best = null;
    let bestScore = -1;
    for (const u of g) {
      const score =
        (u.fullName && norm(u.fullName) !== 'anónimo' ? 1 : 0) +
        (u.photoPath ? 1 : 0) +
        (u.zodiacSign ? 1 : 0) +
        (u.gender != null ? 1 : 0);
      if (score > bestScore) {
        best = u;
        bestScore = score;
      } else if (score === bestScore) {
        best = u; // conservar la última (más reciente)
      }
    }
    bestByKey.set(k, best);
  }

  // 3) Construir set de hashes de perfiles identificados (para colapsar anónimos gemelos)
  const identifiedHashes = new Set();
  for (const u of bestByKey.values()) {
    if (u.userId) identifiedHashes.add(profHash(u));
  }

  // 4) Salida: primero todas las identificadas, luego anónimas cuyo perfil NO colisione con uno identificado
  const out = [];
  for (const u of bestByKey.values()) {
    if (u.userId) out.push(u);
  }
  for (const u of bestByKey.values()) {
    if (!u.userId) {
      const h = profHash(u);
      if (!identifiedHashes.has(h)) out.push(u);
    }
  }

  // 5) Asegurar id estable para React
  return out.map((u) => ({
    ...u,
    id: u.userId || String((u.fullName || 'Anónimo')).trim().toLowerCase(),
  }));
}

function emitAck(event, payload, timeoutMs = 3000) {
  return new Promise((resolve, reject) => {
    try {
      socket.timeout(timeoutMs).emit(event, payload, (err, res) => {
        if (err) return reject(new Error(`${event} ack timeout`));
        resolve(res || { ok: true });
      });
    } catch (e) { reject(e); }
  });
}

// Schedule a single "getRoomUsers" refresh per room (debounced, single delay)
function scheduleRoomRefresh(room, delayMs = 400) {
  if (!room) return;
  if (refreshTimers.has(room)) {
    clearTimeout(refreshTimers.get(room));
    refreshTimers.delete(room);
  }
  const t = setTimeout(() => {
    try { socket.emit("getRoomUsers", room); } catch {}
    refreshTimers.delete(room);
  }, Math.max(0, Number(delayMs) || 0));
  refreshTimers.set(room, t);
}

// Fuerza presencia estable en un room (móviles / wake from background) - throttled/idempotent
async function forcePresence(room) {
  if (!room) return;
  const now = Date.now();

  // Throttle: if we just joined/forced within 1500ms, only refresh list
  const last = lastJoinAt.get(room) || 0;
  if (now - last < 1500) {
    scheduleRoomRefresh(room, 600);
    return;
  }

  if (joinInflight.has(room)) {
    // Another join/force is in-flight; just schedule a refresh afterwards
    scheduleRoomRefresh(room, 900);
    return;
  }

  const uid = getLocalUserId();
  if (!uid) return;

  const prof = pickDefined(getLocalProfile());

  const task = (async () => {
    if (Object.keys(prof).length) {
      try { await emitAck("setProfile", prof); } catch {}
    }
    let res;
    try { res = await emitAck("joinRoom", { room }); } catch {}
    lastJoinAt.set(room, Date.now());
    if (res && res.users) {
      // Evita parpadeo: si el backend ya nos devolvió la lista, propágala sin pedirla 3 veces
      dispatchRoomUsers({ room: canonicalZodiacRoomId(res.room) || room, users: res.users, snapshotAt: res.snapshotAt });
    } else {
      scheduleRoomRefresh(room, 400);
    }
  })().finally(() => {
    joinInflight.delete(room);
  });

  joinInflight.set(room, task);
  await task;
}

// === Auto-repair: if we appear as "Anónimo" in a room, reassert identity + profile and re-join, then refresh list
async function roomUsersAutoRepair(payload) {
  try {
    if (!payload || !payload.users || !Array.isArray(payload.users)) return;
    const room = payload.room;
    const myUserId = (() => { try { return sessionStorage.getItem("userIdSnapshot"); } catch { return null; } })() || getLocalUserId();

    // debounce per room (avoid loops)
    const last = lastRepairByRoom.get(room) || 0;
    if (Date.now() - last < 1500) return;

    // Locate the authenticated user only by the canonical server user id.
    const meRow = payload.users.find((u) => {
      const uid = myUserId && String(myUserId);
      return (
        (u.userId && uid && String(u.userId) === uid) ||
        (u.id && uid && String(u.id) === uid)
      );
    });
    if (!meRow) return;

    const looksAnon = !meRow.fullName || meRow.fullName === "Anónimo";
    if (!looksAnon) return;

    lastRepairByRoom.set(room, Date.now());

    // Re-identify with this tab's snapshot
    if (myUserId) {
      try {
        // Force identify because we detected an anonymous self-row
        if (socket && socket.connected) maybeIdentify(myUserId, { force: true });
      } catch {}
      const prof = pickDefined(getLocalProfile());
      if (Object.keys(prof).length) {
        try { await emitAck("setProfile", prof); } catch {}
      }
      // Re-join the room to refresh adapter snapshot
      try { await emitAck("joinRoom", { room }); } catch {}
    }

    socket.emit("getRoomUsers", room);
  } catch {}
}

/** One-time latch: if this tab has no snapshot yet, adopt from localStorage and freeze into sessionStorage */
function latchUserIdFromLocalStorage() {
  try {
    const existing = sessionStorage.getItem("userIdSnapshot");
    if (existing) return String(existing);
    const raw = localStorage.getItem("me") || localStorage.getItem("user");
    const me = raw ? JSON.parse(raw) : null;
    const fromLS =
      (me && (me._id || me.id || me.userId)) ||
      localStorage.getItem("userId") ||
      null;
    if (fromLS) {
      const val = String(fromLS);
      try { sessionStorage.setItem("userIdSnapshot", val); } catch {}
      return val;
    }
    return null;
  } catch { return null; }
}

// === Identidad local ===
// REGLA: SOLO sessionStorage (identidad propia de la pestaña). No volver a adoptar LS.
function getLocalUserId() {
  try {
    if (typeof window === "undefined") return null;
    const sess = sessionStorage.getItem("userIdSnapshot");
    return sess ? String(sess) : null;
  } catch { return null; }
}

function getLocalProfile() {
  try {
    if (typeof window === "undefined") return lastProfileSnapshot || {};
    const raw = localStorage.getItem("me") || localStorage.getItem("user");
    const me = raw ? JSON.parse(raw) : null;

    const sess = sessionStorage.getItem("profileSnapshot");
    if (!raw && sess) {
      try { lastProfileSnapshot = JSON.parse(sess) || lastProfileSnapshot; } catch {}
    }

    const first = me?.firstName || me?.firstname || me?.given_name || me?.givenName;
    const last  = me?.lastName || me?.lastname || me?.family_name || me?.familyName;
    const composed = [first, last].filter(Boolean).join(" ").trim();

    const fullName =
      me?.fullName ||
      me?.name ||
      composed ||
      (me?.username ? String(me.username) : undefined) ||
      (me?.email ? String(me.email).split("@")[0] : undefined);

    const zodiacSign =
      me?.zodiacSign ||
      me?.sign ||
      me?.zodiac ||
      me?.signo ||
      undefined;

    let photoPath =
      me?.photoPath ||
      me?.photo ||
      me?.avatar ||
      me?.avatarUrl ||
      me?.avatarURL ||
      me?.photoURL ||
      me?.image ||
      undefined;

    if (photoPath && typeof photoPath === "object") {
      if (Array.isArray(photoPath)) {
        photoPath = photoPath[0]?.url || photoPath[0]?.src || String(photoPath[0] || "");
      } else {
        photoPath = photoPath.url || photoPath.src || "";
      }
    }

    const gender =
      typeof me?.gender !== "undefined"
        ? me.gender
        : (typeof me?.sexo !== "undefined" ? me.sexo : undefined);
    
    const seekingGender =
  typeof me?.seekingGender !== "undefined"
    ? me.seekingGender
    : (typeof me?.busco !== "undefined" ? me.busco : undefined);

    const delta = {};
    if (fullName) delta.fullName = fullName;
    if (zodiacSign) delta.zodiacSign = zodiacSign;
    if (photoPath) {
      const str = (photoPath || "").toString().trim();
      if (str) delta.photoPath = str;
    }
    if (typeof gender !== "undefined") delta.gender = gender;
    if (typeof seekingGender !== "undefined") delta.seekingGender = seekingGender;

    lastProfileSnapshot = { ...lastProfileSnapshot, ...delta };
    try { sessionStorage.setItem("profileSnapshot", JSON.stringify(lastProfileSnapshot)); } catch {}
    return lastProfileSnapshot;
  } catch {
    return lastProfileSnapshot || {};
  }
}

// === Socket (singleton global para evitar múltiples conexiones)
const SOCKET_GLOBAL_KEY = "__z10_socket_instance__";
const TAB_ID = getOrCreateTabId();
let baseSocket = null;

if (typeof window !== "undefined" && window[SOCKET_GLOBAL_KEY]) {
  // Reusar la instancia existente y asegurar auth.tabId y token
  baseSocket = window[SOCKET_GLOBAL_KEY];
  try {
    baseSocket.auth = { ...(baseSocket.auth || {}), tabId: TAB_ID, token: getAuthToken() };
    baseSocket.__z10_connectingAuthToken = getAuthToken() || null;
  } catch {}
  // Also ensure query.tabId is set for reused sockets
  try {
    baseSocket.io = baseSocket.io || {};
    baseSocket.io.opts = baseSocket.io.opts || {};
    baseSocket.io.opts.query = baseSocket.io.opts.query || {};
    baseSocket.io.opts.query.tabId = TAB_ID;
  } catch {}
} else {
  // Crear una sola instancia
  baseSocket = io(SOCKET_URL, {
  // AuthContext commits the token through enableSocketAuthentication().
  // Ambient storage alone must never create an authenticated handshake.
  auth: { tabId: TAB_ID },

  // ✅ por si tu backend lee handshake.query.tabId (muchos lo hacen)
  query: { tabId: TAB_ID },

  withCredentials: true,
  transports: ["websocket", "polling"],

  // Connect explicitly below so the pending handshake token is recorded first.
  autoConnect: false,
  forceNew: false,

  reconnection: true,
  reconnectionAttempts: Infinity,
  reconnectionDelay: 800,
  reconnectionDelayMax: 6000,

  timeout: 30000,
  pingInterval: 20000,
  pingTimeout: 30000,
});
  if (typeof window !== "undefined") window[SOCKET_GLOBAL_KEY] = baseSocket;
}

try {
  baseSocket.on('reconnect_attempt', () => {
    try {
      const token = getAuthToken() || null;
      baseSocket.auth = { ...(baseSocket.auth || {}), tabId: TAB_ID, ...(token ? { token } : {}) };
      if (!token) delete baseSocket.auth.token;
      baseSocket.__z10_connectingAuthToken = token;
    } catch {}
    // Also ensure query.tabId is set on reconnect attempts
    try {
      baseSocket.io = baseSocket.io || {};
      baseSocket.io.opts = baseSocket.io.opts || {};
      baseSocket.io.opts.query = baseSocket.io.opts.query || {};
      baseSocket.io.opts.query.tabId = TAB_ID;
    } catch {}
  });
} catch {}

export const socket = baseSocket;

function explicitDisconnectMetadata(source, reason) {
  return {
    source: source || 'unknown_direct_call',
    reason: reason || null,
    stack: (() => {
      try { return new Error().stack || null; } catch { return null; }
    })(),
    socketId: socket?.id || socket?.__z10_lastSocketId || null,
    connected: Boolean(socket?.connected),
    active: Boolean(socket?.active),
    userId: getLocalUserId() || null,
    tabId: TAB_ID,
    authDisabled: Boolean(socket?.__z10_auth_disabled),
    authGeneration: Number(socket?.__z10_auth_generation || 0),
    authVersion: socketAuthVersion(socket),
    committedTokenPresent: Boolean(socket?.__z10_auth_committed_token),
    connectingTokenPresent: Boolean(socket?.__z10_connectingAuthToken),
    connectedTokenPresent: Boolean(socket?.__z10_connectedAuthToken),
    documentVisibilityState: typeof document !== 'undefined' ? document.visibilityState : null,
    timestamp: new Date().toISOString(),
  };
}

function requestExplicitSocketDisconnect({ source, reason } = {}) {
  if (!socket) return false;
  if (socket.__z10_disconnect_intent_inflight) return false;
  socket.__z10_disconnect_intent_inflight = true;
  const intent = explicitDisconnectMetadata(source, reason);
  logSocketDiagnostic('socket:explicit_disconnect_requested', intent);
  let completed = false;
  const finish = () => {
    if (completed) return;
    completed = true;
    try { clearTimeout(timer); } catch {}
    try {
      const rawDisconnect = socket.__z10_raw_disconnect || socket.disconnect;
      rawDisconnect.call(socket);
    } catch {}
    socket.__z10_disconnect_intent_inflight = false;
  };
  const timer = setTimeout(finish, 450);
  if (!socket.connected) {
    finish();
    return true;
  }
  try {
    socket.timeout(350).emit('socket:disconnect_intent', intent, () => finish());
  } catch {
    finish();
  }
  return true;
}

// This catches future direct calls on the authenticated singleton. The
// wrapper deliberately preserves Socket.IO semantics; diagnostics are gated
// by the existing debug switch and never include raw credentials.
try {
  if ((SOCKET_DEV_DIAGNOSTICS || debugSocketsEnabled()) && !socket.__z10_disconnect_instrumented && typeof socket.disconnect === 'function') {
    const rawDisconnect = socket.disconnect.bind(socket);
    socket.__z10_raw_disconnect = rawDisconnect;
    socket.disconnect = function z10InstrumentedSocketDisconnect(...args) {
      logSocketDiagnostic('socket:explicit_disconnect_requested', explicitDisconnectMetadata('unknown_direct_call', 'direct_singleton_disconnect'));
      return rawDisconnect(...args);
    };
    socket.__z10_disconnect_instrumented = true;
  }
} catch {}

try {
  if (!Number.isFinite(Number(socket.__z10_auth_version))) socket.__z10_auth_version = 0;
  if (typeof socket.__z10_auth_disabled !== 'boolean') socket.__z10_auth_disabled = true;
} catch {}

// A socket transport and an authenticated identity are different lifecycles.
// Keep the latter explicitly on the singleton so a reused transport can never
// make callers mistake B's identity for C's after an account switch.
function socketIdentityState() {
  if (!socket.__z10_identity_state) {
    socket.__z10_identity_state = {
      confirmed: null,
      inflight: null,
      generation: 0,
    };
  }
  return socket.__z10_identity_state;
}

function invalidateSocketIdentity(reason = 'invalidated') {
  const state = socketIdentityState();
  const previous = state.confirmed;
  state.generation += 1;
  state.confirmed = null;
  // Do not cancel the wire ACK: its result is discarded by generation checks.
  state.inflight = null;
  logSocketDiagnostic('identity:invalidated', {
    reason,
    expectedUserId: previous?.expectedUserId || null,
    confirmedUserId: previous?.userId || null,
    socketId: socket?.id || socket?.__z10_lastSocketId || null,
    authVersion: socketAuthVersion(socket),
  });
}

function identityStillCurrent(capture) {
  return Boolean(
    capture && socket?.connected && String(socket.id || '') === capture.socketId &&
    getAuthToken() === capture.token && socketAuthVersion(socket) === capture.authVersion &&
    String(getLocalUserId() || '') === capture.expectedUserId &&
    socketIdentityState().generation === capture.generation
  );
}

function waitForSocketConnection() {
  if (socket?.connected) return Promise.resolve(true);
  return new Promise((resolve) => {
    const done = (value) => {
      try { socket.off('connect', onConnect); socket.off('connect_error', onFailure); } catch {}
      resolve(value);
    };
    const onConnect = () => done(true);
    const onFailure = () => done(false);
    socket.on('connect', onConnect);
    socket.on('connect_error', onFailure);
  });
}

/**
 * The only awaitable identity boundary for consumers that need authenticated
 * socket work.  It resolves only after the server ACK has confirmed the
 * requested account on this exact transport/auth version.
 */
export async function ensureSocketIdentity({ expectedUserId, source = 'unspecified' } = {}) {
  const expected = String(expectedUserId || '').trim();
  const token = getAuthToken() || null;
  if (socket?.__z10_auth_disabled || !expected || !token) throw new Error('socket identity requires an enabled authenticated session');

  // The auth UI is authoritative for a same-tab account replacement.  Do not
  // retain the old per-tab snapshot while reconnecting with the new JWT.
  try { sessionStorage.setItem('userIdSnapshot', expected); } catch {}
  const authChanged = syncSocketAuth();
  const state = socketIdentityState();
  const sameConnectedCommittedSession = Boolean(
    socket.connected && !socket.__z10_auth_disabled &&
    String(socket.__z10_connectedAuthUserId || socket.__z10_auth_committed_user_id || '') === expected &&
    Number(socket.__z10_connectedAuthGeneration) === Number(socket.__z10_auth_generation)
  );
  // A refreshed token does not change the server-authenticated namespace.
  // Preserve its confirmed identity while replacing only the future
  // handshake token, so a root rerender cannot manufacture an identify or a
  // transport rotation for the same committed session.
  if (sameConnectedCommittedSession && state.confirmed &&
      state.confirmed.userId === expected &&
      state.confirmed.socketId === String(socket.id || '') &&
      state.confirmed.authVersion === socketAuthVersion(socket)) {
    state.confirmed.token = token;
    state.confirmed.authGeneration = Number(socket.__z10_auth_generation || 0);
    return { ...state.confirmed };
  }
  if (authChanged || (state.confirmed && (
    state.confirmed.userId !== expected || state.confirmed.token !== token ||
    state.confirmed.authVersion !== socketAuthVersion(socket) ||
    state.confirmed.socketId !== String(socket.id || '') ||
    Number(state.confirmed.authGeneration) !== Number(socket.__z10_auth_generation)
  ))) invalidateSocketIdentity(authChanged ? 'auth_changed' : 'identity_changed');

  const confirmed = state.confirmed;
  if (confirmed && confirmed.userId === expected && confirmed.token === token &&
      confirmed.socketId === String(socket.id || '') && confirmed.authVersion === socketAuthVersion(socket)) {
    return { ...confirmed };
  }
  if (state.inflight && state.inflight.expectedUserId === expected && state.inflight.token === token &&
      state.inflight.authVersion === socketAuthVersion(socket)) return state.inflight.promise;

  if (socket.connected && (authChanged || socket.__z10_connectedAuthToken !== token)) reconnectWithCurrentAuth(`identity:${source}`);
  if (!socket.connected) connectWithCurrentAuth({ source: `identity:${source}` });
  if (!await waitForSocketConnection()) throw new Error('socket connection failed before identity confirmation');

  const capture = {
    expectedUserId: expected,
    token,
    authVersion: socketAuthVersion(socket),
    socketId: String(socket.id || ''),
    generation: state.generation,
    authGeneration: Number(socket.__z10_auth_generation || 0),
  };
  const promise = (async () => {
    logSocketDiagnostic('identity:start', { ...capture, source, confirmedUserId: null });
    const ack = await emitAck('identify', { userId: expected });
    const acknowledged = String(ack?.userId || '').trim();
    if (!ack || ack.ok !== true || acknowledged !== expected) throw new Error('server did not confirm requested socket identity');
    if (!identityStillCurrent(capture)) throw new Error('socket identity changed while identifying');
    const value = { userId: expected, expectedUserId: expected, token, authVersion: capture.authVersion,
      socketId: capture.socketId, identityVersion: Number(ack.identityVersion || 0) || null,
      authGeneration: capture.authGeneration };
    state.confirmed = value;
    logSocketDiagnostic('identity:confirmed', { ...capture, source, confirmedUserId: expected, identityVersion: value.identityVersion });
    return { ...value };
  })();
  state.inflight = { ...capture, promise };
  try { return await promise; }
  finally { if (state.inflight?.promise === promise) state.inflight = null; }
}

// Read-only consumer API. UI consumers may observe a confirmed identity but
// must not initiate authentication/registration themselves.
export function getConfirmedSocketIdentity(expectedUserId) {
  const expected = String(expectedUserId || '').trim();
  const confirmed = socketIdentityState().confirmed;
  if (!expected || !confirmed || confirmed.userId !== expected ||
      confirmed.socketId !== String(socket?.id || '') ||
      confirmed.authVersion !== socketAuthVersion(socket)) return null;
  return { ...confirmed };
}

const FOREGROUND_HEALTH_TIMEOUT_MS = 2500;
const CONNECT_ATTEMPT_STALE_MS = 35000;
const FOREGROUND_RECOVERY_EVENT = 'z10:contacts-presence-recovered';
let connectionAttemptSequence = 0;
let targetSocketAttemptSequence = 0;
// Feed subscriptions use dedicated Socket.IO namespaces. They are not the
// authenticated singleton, but a namespace can still be disconnected while
// its shared Manager is already open. Keep an attempt lease per target so
// repeated effect/lifecycle calls cannot send duplicate CONNECT packets.
const targetSocketConnectLeases = new WeakMap();
let foregroundRecoverySequence = 0;
let activeForegroundRecovery = null;

function currentForegroundIdentity() {
  return {
    userId: String(getLocalUserId() || ''),
    token: getAuthToken() || null,
    authVersion: socketAuthVersion(socket),
  };
}

function foregroundContextIsCurrent(context) {
  const current = currentForegroundIdentity();
  return Boolean(
    context &&
    activeForegroundRecovery === context &&
    !context.cancelled &&
    current.userId &&
    current.token &&
    current.userId === context.userId &&
    current.authVersion === context.authVersion &&
    socket?.io?.opts?.reconnection !== false
  );
}

function cancelForegroundRecovery(reason = 'cancelled') {
  foregroundRecoverySequence += 1;
  const active = activeForegroundRecovery;
  activeForegroundRecovery = null;
  if (!active) return;
  active.cancelled = true;
  try { active.cancelWait?.(); } catch {}
  logSocketDiagnostic('socket:foreground_recovery_aborted', {
    source: active.source,
    reason,
    recoveryGeneration: active.generation,
    authenticatedUserId: active.userId || null,
    authVersion: active.authVersion,
    socketId: socket?.id || socket?.__z10_lastSocketId || null,
  });
}

function connectAttemptIsFresh() {
  const attempt = socket?.__z10_connect_lease;
  if (!socket?.__z10_connect_inflight || !attempt) return false;
  const current = currentForegroundIdentity();
  return Boolean(
    socket.io?._readyState === 'opening' &&
    Date.now() - Number(attempt.startedAt || 0) < CONNECT_ATTEMPT_STALE_MS &&
    attempt.userId === current.userId &&
    attempt.authVersion === current.authVersion &&
    attempt.authGeneration === Number(socket.__z10_auth_generation || 0) &&
    attempt.tabId === TAB_ID
  );
}

function clearConnectAttempt({ attemptId = null, reason = 'cleared', clearConnected = false } = {}) {
  try {
    const attempt = socket.__z10_connect_lease;
    if (!attempt) return false;
    if (attemptId !== null && attempt.attemptId !== attemptId) return false;
    if (!clearConnected && attempt.state === 'connected') return false;
    if (attempt.userId !== String(getLocalUserId() || '') ||
        attempt.authGeneration !== Number(socket.__z10_auth_generation || 0) ||
        attempt.tabId !== TAB_ID) return false;
    socket.__z10_connect_inflight = false;
    socket.__z10_connect_attempt = null;
    socket.__z10_connect_lease = null;
    logSocketDiagnostic('socket:connect_lease_released', { attemptId: attempt.attemptId, reason });
    return true;
  } catch { return false; }
}

function connectWithCurrentAuth({ recoveryGeneration = 0, source = 'unspecified' } = {}) {
  try {
    if (!socket) return false;
    if (socket.__z10_auth_disabled || !getAuthToken() || !getLocalUserId()) return false;
    syncSocketAuth();
    presenceDiag('socket:connect-with-current-auth', { socket, source: 'connectWithCurrentAuth' });
    const userId = String(getLocalUserId() || '');
    const authGeneration = Number(socket.__z10_auth_generation || 0);
    const authVersion = socketAuthVersion(socket);
    const managerReadyState = socket.io?._readyState || 'unknown';
    let existing = socket.__z10_connect_lease;
    let matchingLease = existing && existing.userId === userId && existing.authGeneration === authGeneration && existing.authVersion === authVersion && existing.tabId === TAB_ID;
    const suppress = (suppressionReason) => {
      logSocketDiagnostic('socket:connect_suppressed', { source, suppressionReason, existingAttemptId: existing?.attemptId || null, connected: Boolean(socket.connected), active: Boolean(socket.active), managerReadyState, userId, tabId: TAB_ID, authGeneration, authVersion });
      return false;
    };
    if (socket.connected) return suppress('socket_connected');
    if (socket.active) return suppress('socket_active');
    if (managerReadyState === 'opening') return suppress('manager_opening');
    if (matchingLease && existing.state === 'connected') {
      clearConnectAttempt({ attemptId: existing.attemptId, reason: 'namespace_disconnected', clearConnected: true });
      existing = null;
      matchingLease = false;
    }
    if (matchingLease && (existing.state === 'opening' || existing.state === 'connected')) return suppress(`matching_lease_${existing.state}`);
    if (socket.__z10_connect_inflight) return suppress('connection_attempt_inflight');
    logSocketConnectAttempt(socket.__z10_connectingAuthToken || socket.auth?.token || null, source);
    connectionAttemptSequence += 1;
    socket.__z10_connect_inflight = true;
    const attemptId = connectionAttemptSequence;
    socket.__z10_connect_attempt = {
      startedAt: Date.now(),
      attemptId, authVersion, userId, authGeneration, tabId: TAB_ID, state: 'opening',
      recoveryGeneration,
      source,
      socketActive: Boolean(socket.active),
      authDisabled: Boolean(socket.__z10_auth_disabled),
    };
    socket.__z10_connect_lease = socket.__z10_connect_attempt;
    logSocketDiagnostic('socket:connect_requested', {
      socketId: socket.id || socket.__z10_lastSocketId || null,
      connected: Boolean(socket.connected), active: Boolean(socket.active), managerReadyState,
      userId, tabId: TAB_ID, authGeneration, authVersion, attemptId,
      recoveryGeneration,
      source,
    });
    socket.connect();
    return true;
  } catch {
    clearConnectAttempt({ reason: 'connect_throw' });
    return false;
  }
}

export function connectAuthenticatedSocket(source = 'external', targetSocket = socket) {
  // Feed streams retain their own authenticated namespace socket for their
  // subscription protocol. Their per-socket lease is intentionally separate
  // from the authenticated singleton's session-aware lease above.
  if (targetSocket !== socket) {
    if (!targetSocket || targetSocket.connected || targetSocket.active || targetSocket.io?._readyState === 'opening') return false;

    const existingLease = targetSocketConnectLeases.get(targetSocket);
    // A completed namespace lease cannot prevent a later genuine recovery.
    if (existingLease?.state === 'connected' && !targetSocket.connected) {
      targetSocketConnectLeases.delete(targetSocket);
    } else if (existingLease?.state === 'opening') {
      return false;
    }

    targetSocketAttemptSequence += 1;
    const lease = {
      attemptId: targetSocketAttemptSequence,
      source,
      state: 'opening',
      startedAt: Date.now(),
    };
    targetSocketConnectLeases.set(targetSocket, lease);

    const clearListeners = () => {
      try { targetSocket.off?.('connect', onConnect); } catch {}
      try { targetSocket.off?.('connect_error', onConnectError); } catch {}
      try { targetSocket.off?.('disconnect', onDisconnect); } catch {}
    };
    const release = (nextState) => {
      if (targetSocketConnectLeases.get(targetSocket) !== lease) return;
      if (nextState === 'connected') {
        lease.state = 'connected';
        try { targetSocket.off?.('connect_error', onConnectError); } catch {}
        return;
      }
      targetSocketConnectLeases.delete(targetSocket);
      clearListeners();
    };
    const onConnect = () => release('connected');
    const onConnectError = () => release('failed');
    const onDisconnect = () => release('disconnected');

    try {
      targetSocket.on?.('connect', onConnect);
      targetSocket.on?.('connect_error', onConnectError);
      targetSocket.on?.('disconnect', onDisconnect);
      targetSocket.connect();
      return true;
    } catch {
      release('connect_throw');
      return false;
    }
  }
  return connectWithCurrentAuth({ source });
}

function waitForConnectedSocket(context) {
  if (socket?.connected) return Promise.resolve(true);
  return new Promise((resolve) => {
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try {
        socket.off('connect', onConnect);
        socket.off('connect_error', onFailure);
        socket.off('disconnect', onFailure);
      } catch {}
      if (context.cancelWait === cancel) context.cancelWait = null;
      resolve(Boolean(value));
    };
    const onConnect = () => finish(true);
    const onFailure = () => finish(false);
    const cancel = () => finish(false);
    const timer = setTimeout(() => finish(false), CONNECT_ATTEMPT_STALE_MS);
    context.cancelWait = cancel;
    socket.on('connect', onConnect);
    socket.on('connect_error', onFailure);
    socket.on('disconnect', onFailure);
  });
}

function checkAuthenticatedSocketHealth(context) {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (healthy, reason) => {
      if (settled) return;
      settled = true;
      resolve({ healthy: Boolean(healthy), reason });
    };
    logSocketDiagnostic('socket:foreground_health_dispatched', {
      source: context.source,
      recoveryGeneration: context.generation,
      socketId: socket.id || null,
      authenticatedUserId: context.userId,
      authVersion: context.authVersion,
    });
    try {
      socket.timeout(FOREGROUND_HEALTH_TIMEOUT_MS).emit('presence:ping', {}, (error, response) => {
        if (!foregroundContextIsCurrent(context)) return finish(false, 'stale_auth_or_recovery');
        if (error) {
          logSocketDiagnostic('socket:foreground_health_failed', {
            source: context.source,
            reason: error?.message || 'ack_timeout',
            recoveryGeneration: context.generation,
            socketId: socket.id || null,
            authenticatedUserId: context.userId,
            authVersion: context.authVersion,
          });
          return finish(false, 'ack_error');
        }
        if (!response || response.ok !== true) {
          const unauthenticated = response?.error === 'unauthenticated';
          logSocketDiagnostic('socket:foreground_health_failed', {
            source: context.source,
            reason: unauthenticated ? 'unauthenticated_ack' : 'invalid_ack',
            recoveryGeneration: context.generation,
            socketId: socket.id || null,
            authenticatedUserId: context.userId,
            authVersion: context.authVersion,
          });
          return finish(false, unauthenticated ? 'unauthenticated_ack' : 'invalid_ack');
        }
        logSocketDiagnostic('socket:foreground_health_succeeded', {
          source: context.source,
          recoveryGeneration: context.generation,
          socketId: socket.id || null,
          authenticatedUserId: context.userId,
          authVersion: context.authVersion,
        });
        return finish(true, 'ack');
      });
    } catch (error) {
      logSocketDiagnostic('socket:foreground_health_failed', {
        source: context.source,
        reason: error?.message || 'emit_error',
        recoveryGeneration: context.generation,
        socketId: socket?.id || null,
        authenticatedUserId: context.userId,
        authVersion: context.authVersion,
      });
      finish(false, 'emit_error');
    }
  });
}

function notifyForegroundRecoveryComplete(context) {
  try {
    window.dispatchEvent(new CustomEvent(FOREGROUND_RECOVERY_EVENT, {
      detail: {
        socketId: socket?.id || null,
        authVersion: context.authVersion,
        recoveryGeneration: context.generation,
        reason: context.source,
        desiredRooms: joinedRooms.size,
        conversationActive: Boolean(currentConversationId),
      },
    }));
  } catch {}
}

async function completeForegroundRecovery(context) {
  if (socket?.__z10_auth_disabled || !foregroundContextIsCurrent(context) || !socket?.connected) return false;
  const identity = await ensureSocketIdentity({ expectedUserId: context.userId, source: `reconcile:${context.source}` });
  if (!foregroundContextIsCurrent(context) || String(identity?.userId || '') !== context.userId) return false;
  if (!await restoreDesiredRealtimeSubscriptions(context)) return false;
  try { emitPresenceActivity('active'); } catch {}
  try { startPresenceHeartbeat(); } catch {}
  notifyForegroundRecoveryComplete(context);
  logSocketDiagnostic('socket:foreground_recovery_completed', {
    source: context.source,
    recoveryGeneration: context.generation,
    socketId: socket.id || null,
    authenticatedUserId: context.userId,
    authVersion: context.authVersion,
  });
  return true;
}

function waitForSocketDisconnection(timeoutMs = 700) {
  if (!socket?.connected && socket?.io?._readyState !== 'opening') return Promise.resolve(true);
  return new Promise((resolve) => {
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try { socket.off('disconnect', onDisconnect); } catch {}
      resolve(Boolean(value));
    };
    const onDisconnect = () => finish(true);
    const timer = setTimeout(() => finish(!socket?.connected), timeoutMs);
    socket.on('disconnect', onDisconnect);
  });
}

async function replaceStaleTransport(context, reason) {
  if (!foregroundContextIsCurrent(context)) return false;
  logSocketDiagnostic('socket:foreground_transport_replacement_requested', {
    source: context.source,
    reason,
    recoveryGeneration: context.generation,
    socketId: socket.id || socket.__z10_lastSocketId || null,
    authenticatedUserId: context.userId,
    authVersion: context.authVersion,
  });
  try {
    if (socket.connected || socket.io?._readyState === 'opening') {
      requestExplicitSocketDisconnect({ source: `foreground-replace:${context.source}`, reason });
      if (!await waitForSocketDisconnection()) return false;
    }
  } catch {}
  clearConnectAttempt();
  if (!foregroundContextIsCurrent(context)) return false;
  syncSocketAuth();
  if (!foregroundContextIsCurrent(context)) return false;
  context.phase = 'connecting';
  const started = connectWithCurrentAuth({ recoveryGeneration: context.generation, source: `foreground-replace:${context.source}` });
  if (!started && !socket.connected) return false;
  return waitForConnectedSocket(context);
}

async function runForegroundRecovery(context) {
  if (!foregroundContextIsCurrent(context)) return false;
  syncSocketAuth();
  if (!foregroundContextIsCurrent(context)) return false;

  if (socket.connected) {
    context.phase = 'health-check';
    const health = await checkAuthenticatedSocketHealth(context);
    if (!foregroundContextIsCurrent(context)) return false;
    if (!health.healthy) {
      // An explicit unauthenticated ACK is server transport authority, unlike
      // a timeout or generic invalid read. This occurs when the public socket
      // created before login survives the AuthContext transition. Rotate that
      // one transport with the current committed token before identifying.
      if (health.reason === 'unauthenticated_ack') {
        if (!await replaceStaleTransport(context, health.reason)) return false;
        return completeForegroundRecovery(context);
      }
      // A presence snapshot ACK is an application-level read, not transport
      // authority. Treating one timeout/invalid response as a reason to
      // disconnect a live Socket.IO transport creates a same-tab replacement
      // loop: the backend correctly retires the old tab socket, which then
      // looks like another recovery failure. Reconnect only after a real
      // transport disconnect; retain this socket and send its normal activity.
      logSocketDiagnostic('socket:foreground_health_failed_transport_retained', {
        source: context.source,
        reason: health.reason,
        recoveryGeneration: context.generation,
        socketId: socket.id || socket.__z10_lastSocketId || null,
        authenticatedUserId: context.userId,
        authVersion: context.authVersion,
        authGeneration: Number(socket.__z10_auth_generation || 0),
        tabId: TAB_ID,
      });
    }
    return completeForegroundRecovery(context);
  }

  context.phase = 'connecting';
  if (socket.__z10_connect_inflight && !connectAttemptIsFresh()) {
    logSocketDiagnostic('socket:foreground_stale_attempt_invalidated', {
      source: context.source,
      recoveryGeneration: context.generation,
      socketId: socket.id || socket.__z10_lastSocketId || null,
      authenticatedUserId: context.userId,
      authVersion: context.authVersion,
    });
    try {
      if (socket.io?._readyState === 'opening') {
        requestExplicitSocketDisconnect({ source: `foreground-stale-attempt:${context.source}`, reason: 'stale_connect_attempt' });
      }
    } catch {}
    clearConnectAttempt();
  }

  if (!socket.__z10_connect_inflight) {
    if (!connectWithCurrentAuth({ recoveryGeneration: context.generation, source: `foreground:${context.source}` })) return false;
  }
  let connected = await waitForConnectedSocket(context);
  if (!connected && foregroundContextIsCurrent(context) && socket.__z10_connect_inflight && !connectAttemptIsFresh()) {
    connected = await replaceStaleTransport(context, 'connect_attempt_timeout');
  }
  if (!connected || !foregroundContextIsCurrent(context)) return false;
  return completeForegroundRecovery(context);
}

function requestForegroundRecovery(source) {
  try {
    if (!socket || socket.__z10_auth_disabled || socket.io?.opts?.reconnection === false) return Promise.resolve(false);
    syncSocketAuth();
    const identity = currentForegroundIdentity();
    if (!identity.token || !identity.userId) return Promise.resolve(false);

    if (activeForegroundRecovery) {
      const sameIdentity =
        activeForegroundRecovery.userId === identity.userId &&
        activeForegroundRecovery.authVersion === identity.authVersion;
      const abandonedActiveConnect =
        sameIdentity &&
        activeForegroundRecovery.phase === 'connecting' &&
        (!socket.__z10_connect_inflight || !connectAttemptIsFresh());
      if (!abandonedActiveConnect && sameIdentity) {
        logSocketDiagnostic('socket:foreground_recovery_coalesced', {
          source,
          activeSource: activeForegroundRecovery.source,
          recoveryGeneration: activeForegroundRecovery.generation,
          socketId: socket.id || socket.__z10_lastSocketId || null,
          authenticatedUserId: identity.userId,
          authVersion: identity.authVersion,
        });
        return activeForegroundRecovery.promise;
      }
      cancelForegroundRecovery(abandonedActiveConnect ? 'abandoned_connect_attempt' : 'auth_changed');
    }

    foregroundRecoverySequence += 1;
    const context = {
      generation: foregroundRecoverySequence,
      source,
      userId: identity.userId,
      authVersion: identity.authVersion,
      phase: 'starting',
      cancelled: false,
      cancelWait: null,
      promise: null,
    };
    activeForegroundRecovery = context;
    logSocketDiagnostic('socket:foreground_recovery_requested', {
      source,
      recoveryGeneration: context.generation,
      socketId: socket.id || socket.__z10_lastSocketId || null,
      socketConnected: Boolean(socket.connected),
      authenticatedUserId: context.userId,
      authVersion: context.authVersion,
    });
    context.promise = runForegroundRecovery(context).finally(() => {
      if (activeForegroundRecovery === context) activeForegroundRecovery = null;
    });
    return context.promise;
  } catch {
    return Promise.resolve(false);
  }
}

async function restoreDesiredRealtimeSubscriptions(context) {
  if (!foregroundContextIsCurrent(context) || !socket?.connected) return false;
  const version = desiredSubscriptionVersion;
  for (const room of Array.from(joinedRooms)) {
    if (!foregroundContextIsCurrent(context) || version !== desiredSubscriptionVersion) return false;
    presenceDiag('socket:room:rejoin', { socket, source: context.source, extra: { room } });
    let response;
    try { response = await emitAck('joinRoom', { room }); } catch {}
    lastJoinAt.set(room, Date.now());
    if (response?.users) {
      dispatchRoomUsers({ room: canonicalZodiacRoomId(response.room) || room, users: response.users, snapshotAt: response.snapshotAt });
    } else {
      try { socket.emit('getRoomUsers', room); } catch {}
    }
  }
  if (!foregroundContextIsCurrent(context) || version !== desiredSubscriptionVersion) return false;
  if (currentConversationId) {
    const conversationId = String(currentConversationId);
    socket.emit('joinConversation', { conversationId, userId: context.userId });
    try { await requestTypingSnapshot(conversationId); } catch {}
  }
  return foregroundContextIsCurrent(context) && version === desiredSubscriptionVersion;
}

/** One bounded, authenticated transport/application reconciliation pass. */
export async function reconcileAuthenticatedRealtimeSession({ source = 'unspecified', expectedUserId } = {}) {
  const committed = String(socket?.__z10_auth_committed_user_id || '').trim();
  const expected = String(expectedUserId || committed).trim();
  if (socket?.__z10_auth_disabled || !expected || !committed || expected !== committed) return false;
  // AuthContext is the authority during process/cold restoration. Establish
  // this tab's snapshot before transport recovery reads it; never adopt an
  // arbitrary localStorage identity inside the socket layer.
  try { sessionStorage.setItem('userIdSnapshot', expected); } catch {}
  return requestForegroundRecovery(source);
}

export function setRealtimeBackgroundState() {
  try { emitPresenceActivity('away'); } catch {}
  try { stopPresenceHeartbeat(); } catch {}
  // Keep Socket.IO's normal reconnection policy enabled while the app is
  // backgrounded. Mobile browsers may suspend the transport and deliver the
  // disconnect/reconnect later; disabling manager recovery here leaves a
  // still-mounted room with no way to restore its membership on foreground.
}

export function setRealtimeNetworkOffline() {
  try {
    cancelForegroundRecovery('network_offline');
    stopPresenceHeartbeat();
    stopAllTyping({ notifyServer: false });
    clearConnectAttempt({ reason: 'network_offline' });
    socket.io.opts.reconnection = false;
    socket.io.reconnection?.(false);
    if (socket.connected || socket.active || socket.io?._readyState === 'opening') {
      logSocketDiagnostic('socket:network_offline_transport_paused', {
        socketConnected: Boolean(socket.connected),
        authGeneration: Number(socket.__z10_auth_generation || 0),
      });
      socket.disconnect();
    }
    return true;
  } catch { return false; }
}

export function setRealtimeNetworkOnline() {
  try {
    if (socket.__z10_auth_disabled || !socket.__z10_auth_committed_token || !socket.__z10_auth_committed_user_id) return false;
    socket.io.opts.reconnection = true;
    socket.io.reconnection?.(true);
    syncSocketAuth();
    return true;
  } catch { return false; }
}

export function getDesiredRealtimeSubscriptions() {
  return Object.freeze({ rooms: Object.freeze(Array.from(joinedRooms)), conversationId: currentConversationId ? String(currentConversationId) : null });
}

function reconnectWithCurrentAuth(source = 'unspecified') {
  try {
    if (!socket) return false;
    if (socket.__z10_auth_disabled || !getAuthToken() || !getLocalUserId()) return false;
    const currentToken = getAuthToken() || null;
    const connectedToken = socket.__z10_connectedAuthToken || null;
    const currentUserId = String(getLocalUserId() || '');
    const authGeneration = Number(socket.__z10_auth_generation || 0);
    const connectedGeneration = Number(socket.__z10_connectedAuthGeneration);
    const connectedUserId = String(socket.__z10_connectedAuthUserId || socket.__z10_auth_committed_user_id || '');
    presenceDiag('socket:reconnect-check', { socket, source, connectedToken, pendingToken: currentToken, extra: { tokenChanged: Boolean(currentToken && connectedToken && currentToken !== connectedToken) } });
    logSocketDiagnostic('socket:reconnect_with_current_auth', {
      socketId: socket.id || socket.__z10_lastSocketId || null,
      authenticatedUserId: getLocalUserId() || null,
      socketConnected: Boolean(socket.connected),
      tokenChanged: Boolean(currentToken && connectedToken && currentToken !== connectedToken),
      authVersion: socketAuthVersion(socket),
      source,
    });
    if (!socket.connected) return connectWithCurrentAuth({ source: `reconnect:${source}` });
    const sameCommittedSession = !socket.__z10_auth_disabled &&
      currentUserId && connectedUserId === currentUserId &&
      Number.isFinite(authGeneration) && authGeneration === connectedGeneration;
    if (sameCommittedSession) {
      // Keep the old handshake credential as historical transport state and
      // retain the latest JWT only in socket.auth for a future natural
      // reconnect. This is expected after token refresh and is never a
      // reason to rotate a healthy namespace connection.
      socket.__z10_connectingAuthToken = currentToken;
      socket.auth = { ...(socket.auth || {}), tabId: TAB_ID, token: currentToken };
      logSocketDiagnostic('socket:token_updated_transport_retained', {
        source,
        socketId: socket.id || socket.__z10_lastSocketId || null,
        userId: currentUserId,
        authGeneration,
        authVersion: socketAuthVersion(socket),
        connected: true,
        tabId: TAB_ID,
      });
      return false;
    }
    if (!currentToken || !connectedToken || currentToken === connectedToken) {
      syncSocketAuth();
      return false;
    }
    if (socket.__z10_auth_reconnect_pending) return false;
    cancelForegroundRecovery('token_refresh');
    socket.__z10_auth_reconnect_pending = true;
    presenceDiag('socket:reconnect-disconnect', { socket, source, connectedToken, pendingToken: currentToken, reason: 'token_changed' });
    logSocketDiagnostic('socket:disconnect_requested', {
      reason: 'token_changed',
      source,
      socketId: socket.id || socket.__z10_lastSocketId || null,
      socketConnected: Boolean(socket.connected),
      authenticatedUserId: getLocalUserId() || null,
      authVersion: socketAuthVersion(socket),
    });
    requestExplicitSocketDisconnect({ source: `token-rotation:${source}`, reason: 'token_changed' });
    clearConnectAttempt();
    return connectWithCurrentAuth({ source: `reconnect:${source}` });
  } catch {
    return false;
  }
}

// Capture the actual handshake credential before other connect listeners
// refresh socket.auth for a possible future transport.
if (!socket.__z10_handshake_auth_bound) {
  socket.__z10_handshake_auth_bound = true;
  socket.on('connect', () => {
    socket.__z10_lastSocketId = socket.id || null;
    logSocketDiagnostic('socket:connect', {
      socketId: socket.id || null,
      authenticatedUserId: getLocalUserId() || null,
      socketConnected: Boolean(socket.connected),
      authVersion: socketAuthVersion(socket),
    });
    socket.__z10_connectedAuthToken =
      socket.__z10_connectingAuthToken || socket.auth?.token || null;
    socket.__z10_connectedAuthGeneration = Number(socket.__z10_auth_generation || 0);
    socket.__z10_connectedAuthUserId = String(socket.__z10_auth_committed_user_id || getLocalUserId() || '');
    const lease = socket.__z10_connect_lease;
    if (lease && lease.userId === String(getLocalUserId() || '') &&
        lease.authGeneration === Number(socket.__z10_auth_generation || 0) && lease.tabId === TAB_ID) {
      lease.state = 'connected';
      socket.__z10_connect_inflight = false;
      socket.__z10_connect_attempt = null;
      logSocketDiagnostic('socket:connect_committed', {
        attemptId: lease.attemptId, socketId: socket.id || null, userId: lease.userId,
        tabId: TAB_ID, authGeneration: lease.authGeneration, authVersion: lease.authVersion,
      });
    }
    socket.__z10_auth_reconnect_pending = false;
    presenceDiag('socket:connected', { socket, source: 'socket connect listener' });
  });
  socket.on('disconnect', (reason) => {
    presenceDiag('socket:disconnected', { socket, source: 'socket disconnect listener', reason });
    logSocketDiagnostic('socket:disconnect', {
      disconnectReason: reason || null,
      socketId: socket.id || socket.__z10_lastSocketId || null,
      authenticatedUserId: getLocalUserId() || null,
      socketConnected: Boolean(socket.connected),
      authVersion: socketAuthVersion(socket),
    });
    clearConnectAttempt({ reason: 'disconnect', clearConnected: true });
  });
  socket.on('connect_error', (error) => {
    logSocketDiagnostic('socket:connect_error', {
      error: error?.message || String(error || ''),
      socketId: socket.id || socket.__z10_lastSocketId || null,
      authenticatedUserId: getLocalUserId() || null,
      socketConnected: Boolean(socket.connected),
      authVersion: socketAuthVersion(socket),
    });
    clearConnectAttempt({ reason: 'connect_error' });
    socket.__z10_auth_reconnect_pending = false;
  });
}

if (!socket.io.__z10_reconnect_diagnostics_bound) {
  socket.io.__z10_reconnect_diagnostics_bound = true;
  socket.io.on('reconnect_attempt', (attempt) => {
    logSocketDiagnostic('socket:reconnect_attempt', {
      attempt: Number(attempt || 0),
      socketId: socket.id || socket.__z10_lastSocketId || null,
      authenticatedUserId: getLocalUserId() || null,
      socketConnected: Boolean(socket.connected),
      authVersion: socketAuthVersion(socket),
    });
  });
  socket.io.on('reconnect', (attempt) => {
    logSocketDiagnostic('socket:reconnect', {
      attempt: Number(attempt || 0),
      socketId: socket.id || socket.__z10_lastSocketId || null,
      authenticatedUserId: getLocalUserId() || null,
      socketConnected: Boolean(socket.connected),
      authVersion: socketAuthVersion(socket),
    });
  });
}

// Logout must tear down this tab's authenticated transport.  Clearing only
// localStorage is insufficient because Socket.IO keeps handshake auth and may
// reconnect with the previous JWT.
export function clearSocketAuthentication({ userId, reason = 'logout' } = {}) {
  try {
    const authoritativeReasons = new Set(['logout', 'account_deleted', 'committed_account_switch', 'session_invalidated', 'token_revoked', 'application_shutdown']);
    if (!authoritativeReasons.has(reason)) {
      logSocketDiagnostic('socket:disconnect_rejected', {
        source: 'clearSocketAuthentication', reason,
        socketId: socket?.id || socket?.__z10_lastSocketId || null,
        userId: getLocalUserId() || (userId ? String(userId) : null),
        authGeneration: Number(socket?.__z10_auth_generation || 0),
      });
      return false;
    }
    // Close the gap while AuthContext awaits the HTTP logout request.
    socket.__z10_auth_disabled = true;
    socket.__z10_auth_generation = Number(socket.__z10_auth_generation || 0) + 1;
    socket.__z10_auth_committed_token = null;
    socket.__z10_auth_committed_user_id = null;
    cancelForegroundRecovery(reason);
    // An authoritative auth teardown cancels any prior-generation lease;
    // tuple matching is intentionally bypassed because the generation has
    // just advanced.
    socket.__z10_connect_inflight = false;
    socket.__z10_connect_attempt = null;
    socket.__z10_connect_lease = null;
    // Invalidate before clearing storage/disconnecting so an ACK or heartbeat
    // callback from the old account can never become current after logout.
    invalidateSocketIdentity(reason);
    logSocketDiagnostic('socket:clear_authentication', {
      reason,
      socketId: socket?.id || socket?.__z10_lastSocketId || null,
      authenticatedUserId: getLocalUserId() || (userId ? String(userId) : null),
    });
    presenceDiag('socket:clear-authentication:start', { socket, source: 'clearSocketAuthentication', userId, reason });
    try { stopPresenceHeartbeat(); } catch {}
    try { stopAllTyping(); } catch {}
    try { unbindPresenceDomEvents(); } catch {}
    joinedRooms.clear();
    currentConversationId = null;
    desiredSubscriptionVersion += 1;
    lastProfileSnapshot = {};
    try {
      sessionStorage.removeItem('userIdSnapshot');
      sessionStorage.removeItem('profileSnapshot');
    } catch {}
    try {
      socket.auth = {};
      socket.__z10_connectingAuthToken = null;
      socket.__z10_connectedAuthToken = null;
      clearConnectAttempt();
      socket.__z10_auth_reconnect_pending = false;
      socket.io.opts.reconnection = false;
      presenceDiag('socket:clear-authentication:disconnect', { socket, source: 'clearSocketAuthentication', userId, reason });
      requestExplicitSocketDisconnect({ source: reason === 'logout' ? 'logout' : 'auth_cleanup', reason });
    } catch {}
    return true;
  } catch { return false; }
}

// ===== Global presence heartbeat (mantiene la presencia viva entre pantallas) =====
// IMPORTANT: en dev con HMR, variables module-scoped se reinician y pueden dejar intervals viejos vivos.
// Por eso persistimos el id del intervalo y el ms en `window` para que sea realmente singleton por pestaña.
const __Z10_PRESENCE_INTERVAL_KEY = '__z10_presence_interval_id__';
const __Z10_PRESENCE_INTERVAL_MS_KEY = '__z10_presence_interval_ms__';

function __getPresenceIntervalId() {
  try {
    if (typeof window === 'undefined') return null;
    return window[__Z10_PRESENCE_INTERVAL_KEY] || null;
  } catch { return null; }
}
function __setPresenceIntervalId(id) {
  try {
    if (typeof window === 'undefined') return;
    window[__Z10_PRESENCE_INTERVAL_KEY] = id;
  } catch {}
}
function __getPresenceIntervalMs(defaultMs = 30000) {
  try {
    if (typeof window === 'undefined') return defaultMs;
    const v = window[__Z10_PRESENCE_INTERVAL_MS_KEY];
    const n = Number(v);
    return Number.isFinite(n) && n >= 15000 ? n : defaultMs;
  } catch { return defaultMs; }
}
function __setPresenceIntervalMs(ms) {
  try {
    if (typeof window === 'undefined') return;
    window[__Z10_PRESENCE_INTERVAL_MS_KEY] = ms;
  } catch {}
}

function __isTabActive() {
  try { return typeof document === 'undefined' ? true : document.visibilityState === 'visible'; } catch { return true; }
}

function startPresenceHeartbeat() {
  presenceDiag('socket:heartbeat:start', { socket, source: 'startPresenceHeartbeat' });
  try {
    const existing = __getPresenceIntervalId();
    // The authenticated application lifecycle may be mounted more than once
    // in development. A heartbeat is a tab singleton, not an effect-owned
    // resource, so preserve the existing cadence instead of replacing it.
    if (existing) return existing;
  } catch {}

  const intervalMs = __getPresenceIntervalMs(30000);

  const id = setInterval(() => {
    try {
      if (!__isTabActive()) return; // no spamear si está en background

      if (socket && socket.connected && getLocalUserId()) {
        presenceDiag('socket:presence:ping', { socket, source: 'heartbeat' });
        socket.emit('presence:ping');
      }
    } catch {}
  }, intervalMs);

  __setPresenceIntervalId(id);
  return id;
}

function stopPresenceHeartbeat() {
  presenceDiag('socket:heartbeat:stop', { socket, source: 'stopPresenceHeartbeat' });
  try {
    const existing = __getPresenceIntervalId();
    if (existing) {
      try { clearInterval(existing); } catch {}
    }
  } catch {}
  try { __setPresenceIntervalId(null); } catch {}
}

export function setPresenceInterval(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n < 15000) return; // bounded local cadence
  __setPresenceIntervalMs(n);
  // A deliberate cadence configuration change is the only caller allowed to
  // replace the singleton interval.
  if (socket && socket.connected) {
    stopPresenceHeartbeat();
    startPresenceHeartbeat();
  }
}

// ===== DOM presence signals (away/back/bye) - bind once per tab =====
// IMPORTANT: en dev con HMR, este módulo puede re-ejecutarse; persistimos el bind en window.
// ===== DOM presence signals (away/back/bye) - bind once per tab =====
const __Z10_PRESENCE_DOM_BOUND_KEY = '__z10_presence_dom_bound__';
const __Z10_PRESENCE_DOM_HANDLERS_KEY = '__z10_presence_dom_handlers__';
let lastPresenceActivitySignal = null;

function emitPresenceActivity(nextState) {
  const normalized = nextState === 'away' ? 'away' : 'active';
  if (!socket?.connected || !getLocalUserId()) return false;
  if (lastPresenceActivitySignal === normalized) return false;
  lastPresenceActivitySignal = normalized;
  socket.emit(normalized === 'away' ? 'presence:away' : 'presence:back');
  return true;
}

// guards para evitar duplicar listeners globales
const __Z10_STORAGE_BOUND_KEY = '__z10_storage_bound__';

function bindPresenceDomEvents() {
  if (typeof window === "undefined") return;
  try {
    if (window[__Z10_PRESENCE_DOM_BOUND_KEY]) return;
    window[__Z10_PRESENCE_DOM_BOUND_KEY] = true;
  } catch {}

  const safeEmit = (event) => {
    try {
      socket && socket.emit && socket.emit(event);
    } catch {}
  };

  // Blur/focus → away/back signals
  // Before closing the tab/window → quick bye
  const onBeforeUnload = () => {
    // Best-effort explicit offline hint before transport closes
    safeEmit('presence:bye');
  };

  window.addEventListener('beforeunload', onBeforeUnload);
  try { window[__Z10_PRESENCE_DOM_HANDLERS_KEY] = { onBeforeUnload }; } catch {}
  // ==== LATCH: evitar identify() spam en dev/hmr ====
  if (!socket.__z10_identify_guard_bound) {
    socket.__z10_identify_guard_bound = true;
    let sentIdentifyRef = false;
    socket.on('connect', () => {
      if (sentIdentifyRef) return;
      sentIdentifyRef = true;
      // solo aseguramos que esta pestaña tenga snapshot propio;
      // el handler global de socket "connect" hace el identify/setProfile/joinRoom
      latchUserIdFromLocalStorage();
    });
  }
  // ==== fin latch ====
}

function unbindPresenceDomEvents() {
  if (typeof window === 'undefined') return;
  try {
    const handlers = window[__Z10_PRESENCE_DOM_HANDLERS_KEY];
    if (handlers) {
      window.removeEventListener('beforeunload', handlers.onBeforeUnload);
    }
    delete window[__Z10_PRESENCE_DOM_HANDLERS_KEY];
    window[__Z10_PRESENCE_DOM_BOUND_KEY] = false;
  } catch {}
}

// Transport-level restoration only. Authenticated identity and heartbeat are
// owned by AuthenticatedSocketLifecycle, not by this singleton's transport
// callbacks or a screen component.
if (!socket.__z10_connect_bound) {
  socket.on("connect", async () => {
    latchUserIdFromLocalStorage();
    try { syncSocketAuth(); } catch {}
    const userId = getLocalUserId();
    if (!userId) {
      // The authenticated application lifecycle will establish identity once
      // auth state is ready. Do not create a delayed competing bootstrap.
      try { stopPresenceHeartbeat(); } catch {}
      return;
    }
    try {
      try { sessionStorage.setItem("userIdSnapshot", String(userId)); } catch {}
      const prof = pickDefined(getLocalProfile());
      if (Object.keys(prof).length) await emitAck("setProfile", prof);
    } catch {}
  });

  socket.off("identified").on("identified", (payload) => {
    // Server payload is `{ userId, via?, identityVersion? }`; tolerate the
    // historical nested form during rolling deploys, but never infer it.
    const acknowledgedUserId = String(payload?.userId || payload?.identity?.userId || '').trim() || null;
    const expectedUserId = String(getLocalUserId() || '').trim() || null;
    if (acknowledgedUserId && acknowledgedUserId === expectedUserId && socket.connected) {
      const state = socketIdentityState();
      state.confirmed = {
        userId: acknowledgedUserId,
        expectedUserId,
        token: getAuthToken() || null,
        authVersion: socketAuthVersion(socket),
        socketId: String(socket.id || ''),
        identityVersion: Number(payload?.identityVersion || 0) || null,
        authGeneration: Number(socket.__z10_auth_generation || 0),
      };
    }
    logSocketDiagnostic('identify:server_event', {
      acknowledgedUserId,
      via: payload?.via || null,
      socketId: socket.id || null,
      socketConnected: Boolean(socket.connected),
      authVersion: socketAuthVersion(socket),
    });
  });
  socket.off("authCleared").on("authCleared", () => {
  // no auto-reidentify
});
socket.off("authError").on("authError", () => {
  // no auto-reidentify
});

  socket.__z10_connect_bound = true; // marca para no re-encadenar
  // Opcional: log connect_error para debugging de token inválido
  if (!socket.__z10_connect_error_bound) {
    socket.on('connect_error', (err) => {
      try { console.warn('[socket] connect_error', err?.message || err); } catch {}
    });
    socket.__z10_connect_error_bound = true;
  }
  if (!socket.__presence_disconnect_bound) {
    socket.on('disconnect', () => {
      lastPresenceActivitySignal = null;
      invalidateSocketIdentity('disconnect');
      try { stopPresenceHeartbeat(); } catch {}
      try { stopAllTyping({ notifyServer: false }); } catch {}
    });
    socket.__presence_disconnect_bound = true;
  }
  if (!socket.__z10_presence_req_bound) {
    socket.on('presence:request-ping', () => {
      try {
        if (socket && socket.connected && getLocalUserId()) {
          socket.emit('presence:ping');
        }
      } catch {}
    });
    socket.__z10_presence_req_bound = true;
  }
  // Bind DOM presence events once per tab
  try { bindPresenceDomEvents(); } catch {}
}

// Cuando el backend invalida la sesión (misma cuenta en otro dispositivo)
if (!socket.__z10_session_invalidated_bound) {
  socket.on('sessionInvalidated', (data) => {
    logSocketDiagnostic('socket:session_invalidated', {
      reason: data?.reason || 'signed_in_elsewhere',
      socketId: socket.id || socket.__z10_lastSocketId || null,
      authenticatedUserId: getLocalUserId() || null,
    });
    // Limpia identidad *solo* de esta pestaña
    try {
      sessionStorage.removeItem('userIdSnapshot');
      sessionStorage.removeItem('profileSnapshot');
    } catch {}

    // Salir de rooms y cerrar socket para evitar reapariciones fantasma
    try { joinedRooms.forEach((room) => socket.emit('leaveRoom', room)); } catch {}
    // Best-effort removal; the server derives the owner from this socket.
    try { socket.emit('presence:bye'); } catch {}
    try { stopPresenceHeartbeat(); } catch {}
    try { stopAllTyping(); } catch {}
    try { unbindPresenceDomEvents(); } catch {}
    try { requestExplicitSocketDisconnect({ source: 'session_invalidated', reason: 'session_invalidated' }); } catch {}

    // Notificar a la UI (React) con un evento global; evitamos re-encadenar listeners de socket
    try {
      window.dispatchEvent(new CustomEvent('z10:sessionInvalidated', {
        detail: {
          reason: data?.reason || 'signed_in_elsewhere',
          userId: data?.userId || null,
          kicker: data?.kicker || null,
        }
      }));
    } catch {}

    // Log silencioso y con rate-limit (solo en dev o si localStorage.debugSockets==='1')
    try {
      const now = Date.now();
      const wantDebug = debugSocketsEnabled();
      if (wantDebug && now - __z10_lastSessionInvalidationLog > 30000) {
        console.debug('[socket] sessionInvalidated', {
          reason: String(data?.reason || 'unknown').slice(0, 80),
          authenticatedIdentityPresent: Boolean(data?.userId),
        });
        __z10_lastSessionInvalidationLog = now;
      }
    } catch {}
  });
  socket.__z10_session_invalidated_bound = true;
}

// Bind interno roomUsers: una sola vez por socket (no por módulo)
if (!socket.__z10_roomUsers_bound) {
  socket.on("roomUsers", (payload) => {
    roomUsersAutoRepair(payload);
    const cleanUsers = dedupeUsers(payload?.users || []);
    dispatchRoomUsers({
      room: canonicalZodiacRoomId(payload?.room) || payload?.room,
      users: cleanUsers,
      snapshotAt: payload?.snapshotAt,
    });
  });
  socket.__z10_roomUsers_bound = true;
}

if (!socket.__z10_zodiac_room_counts_bound) {
  socket.on('zodiacRooms:counts', (payload) => {
    try { dispatchZodiacRoomCounts(payload || {}); } catch {}
  });
  socket.__z10_zodiac_room_counts_bound = true;
}

// Bind userJoined / userLeft once (coalesce + refresh roomUsers)
if (!socket.__z10_user_events_bound) {
  socket.on("userJoined", (payload) => {
    if (!payload || !payload.room) return;
    const sid = payload.userId || payload.socketId || payload.id;
    // drop rapid duplicates for the same socket in the same room
    if (!seenRecently(lastJoinedSeen, payload.room, String(sid))) {
      // schedule a single refresh to get the authoritative list
      scheduleRoomRefresh(payload.room, 120);
      // fan-out (optional listeners)
      dispatchUserJoined(payload);
    }
  });
  socket.on("userLeft", (payload) => {
    if (!payload || !payload.room) return;
    const sid = payload.userId || payload.socketId || payload.id;
    if (!seenRecently(lastLeftSeen, payload.room, String(sid))) {
      scheduleRoomRefresh(payload.room, 120);
      dispatchUserLeft(payload);
    }
  });

    if (!socket.__z10_message_bound) {
    socket.on('message', (payload) => {
      try { dispatchMessage(payload || {}); } catch {}
    });
    socket.__z10_message_bound = true;
  }

  if (!socket.__z10_likes_bound) {
    socket.on('likesUpdate', (payload) => {
      try { dispatchLikesUpdate(payload || {}); } catch {}
    });
    socket.__z10_likes_bound = true;
  }

  if (!socket.__z10_conv_presence_bound) {
    socket.on('conversationPresence', (payload) => {
      try { dispatchConversationPresence(payload || {}); } catch {}
    });
    socket.on('conversationPresenceSnapshot', (payload) => {
      try { dispatchConversationPresenceSnapshot(payload || {}); } catch {}
    });
    socket.__z10_conv_presence_bound = true;
  }

  if (!socket.__z10_contacts_bound) {
    socket.on('contacts:request-received', (payload) => {
      try { dispatchContactRequestReceived(payload || {}); } catch {}
    });
    socket.on('contacts:request-accepted', (payload) => {
      try { dispatchContactAccepted(payload || {}); } catch {}
    });
    socket.on('contacts:request-declined', (payload) => {
      try { dispatchContactDeclined(payload || {}); } catch {}
    });
    socket.on('contacts:removed', (payload) => {
      try { dispatchContactRemoved(payload || {}); } catch {}
    });
    socket.__z10_contacts_bound = true;
  }

  if (!socket.__z10_presence_update_bound) {
    socket.on('presence:update', (payload) => {
      try { dispatchPresenceUpdate(payload || {}); } catch {}
    });
    socket.__z10_presence_update_bound = true;
  }

  if (!socket.__z10_presence_user_bound) {
    socket.on('presence:user', (payload) => {
      try { dispatchUserPresence(payload || {}); } catch {}
    });
    socket.__z10_presence_user_bound = true;
  }

  socket.__z10_user_events_bound = true;
}

// Bind conversation + badge events once
if (!socket.__z10_conv_events_bound) {
  socket.on('badge:update', (payload) => {
  const p = payload || {};
  // payload shape expected: { fromUserId, unread, lastMessage, conversationId, at }
  dispatchBadgeUpdate(p);

  // 🔔 puente hacia notificación unificada (Home campana)
  try {
    const unreadCount = Number(p.unread);
    dispatchUnifiedNotification({
      kind: 'chat',
      type: 'chat',
      source: 'chat',
      conversationId: p.conversationId,
      unreadCount: Number.isFinite(unreadCount) ? unreadCount : undefined,
      at: p.at || Date.now(),
      lastMessage: p.lastMessage,
      fromUserId: p.fromUserId,
    });
  } catch {}
});

  socket.on('conversation:new', (payload) => {
  const p = payload || {};
  // payload shape expected: { conversationId, message, fromUserId, toUserId }
  dispatchConversationPreview(p);

  // 🔔 puente unificado (nuevo chat)
  try {
    dispatchUnifiedNotification({
      kind: 'chat',
      type: 'chat',
      source: 'chat',
      conversationId: p.conversationId,
      unreadCount: 1,
      at: Date.now(),
      message: p.message,
      fromUserId: p.fromUserId,
      toUserId: p.toUserId,
    });
  } catch {}
});

  // Nuevo: fan-out de mensajes 1 a 1 sin off/on
  socket.on('newConversationMessage', (payload) => {
    const p = payload || {};
    try { dispatchNewConversationMessage(p) // 🔔 puente unificado (mensaje 1 a 1)
try {
  dispatchUnifiedNotification({
    kind: 'chat',
    type: 'chat',
    source: 'chat',
    conversationId: p.conversationId,
    unreadCount: p.unreadCount != null ? p.unreadCount : undefined,
    at: p.at || Date.now(),
    message: p.message,
    fromUserId: p.fromUserId,
    toUserId: p.toUserId,
  });
} catch {}; } catch {}
    // Si estoy en esa conversación y la pestaña está visible, marcar como leído
    if (p.conversationId && currentConversationId && String(p.conversationId) === String(currentConversationId) && isDocumentVisible()) {
      markConversationRead(p.conversationId, getLocalUserId());
    }
  });

  // Foro: notificaciones realtime (likes / replies)
  if (!socket.__z10_forum_notif_bound) {
    socket.on('forum:notification', (payload) => {
  const p = payload || {};
  try { dispatchForumNotification(p); } catch {}

  // 🔔 puente unificado (foro)
  try {
    dispatchUnifiedNotification({
      kind: 'forum',
      type: 'forum',
      source: 'forum',
      unreadCount: p.unreadCount != null ? p.unreadCount : 1,
      at: p.at || Date.now(),
      ...p,
    });
  } catch {}
});
    socket.__z10_forum_notif_bound = true;
  }

  // Notificaciones unificadas (foro + chat) para la campana del Home
  if (!socket.__z10_unified_notif_bound) {
    const handler = (payload) => {
      try { dispatchUnifiedNotification(payload || {}); } catch {}
    };
    socket.on('unifiedNotification', handler);
    socket.on('unified:notification', handler);
    socket.__z10_unified_notif_bound = true;
  }

  socket.__z10_conv_events_bound = true;
}

// Si otra pestaña cambia el LS, **NO** cambiamos la identidad de esta pestaña si ya tiene snapshot propio.
if (typeof window !== "undefined" && !window[__Z10_STORAGE_BOUND_KEY]) {
  window[__Z10_STORAGE_BOUND_KEY] = true;
  window.addEventListener("storage", (e) => {
    if (e.key !== "me" && e.key !== "user" && e.key !== "userId") return;

    const sessUser = (() => { try { return sessionStorage.getItem("userIdSnapshot"); } catch { return null; } })();
    // Esta pestaña está “pinneada” a su identidad propia; ignorar LS.
    if (sessUser) {
      // This tab is pinned to its own session identity; ignore cross-tab LS changes.
      return;
    }

    // Esta pestaña NO tiene snapshot => adopta LS (primer uso)
    const lsUser = (() => {
      try {
        const raw = localStorage.getItem("me") || localStorage.getItem("user");
        const me = raw ? JSON.parse(raw) : null;
        return (
          (me && (me._id || me.id || me.userId)) ||
          localStorage.getItem("userId") ||
          null
        );
      } catch { return null; }
    })();

    if (lsUser) {
      // adoptamos y quedamos "pinneados" a esta identidad
      const val = String(lsUser);
      try { sessionStorage.setItem("userIdSnapshot", val); } catch {}
      if (!socket.connected) connectWithCurrentAuth();
      try { maybeIdentify(val); } catch {}
      // snapshot mínimo de perfil para evitar "Anónimo" transitorio
      const prof = pickDefined(getLocalProfile());
      if (Object.keys(prof).length) socket.emit("setProfile", prof);

      (async () => {
        for (const room of Array.from(joinedRooms)) {
          try { await forcePresence(room); } catch {}
        }
      })();
    } else {
      // LS se limpió y esta pestaña no tenía snapshot propio -> salimos de rooms.
      try { joinedRooms.forEach((room) => socket.emit("leaveRoom", room)); } catch {}
    }
  });

  // Mantener auth.token sincronizado cuando cambie el JWT en localStorage
  if (!window.__z10_token_storage_bound) {
    window.__z10_token_storage_bound = true;
    const refreshHandshakeToken = () => {
      const changed = syncSocketAuth();
      if (changed && socket?.connected) reconnectWithCurrentAuth('token-storage');
    };
    window.addEventListener('storage', (e) => {
      if (e.key === 'token' || e.key === 'accessToken' || e.key === 'authToken' || e.key === 'jwt') {
        refreshHandshakeToken();
      }
    });
    window.addEventListener('token:changed', refreshHandshakeToken);
  }
}

// === API públicas
export function setSocketProfile({ fullName, zodiacSign, photoPath, gender, seekingGender } = {}) {
  const prof = pickDefined({
    fullName,
    zodiacSign,
    photoPath,
    gender: typeof gender !== "undefined" ? gender : undefined,
    seekingGender: typeof seekingGender !== "undefined" ? seekingGender : undefined,
  });
  if (Object.keys(prof).length) socket.emit("setProfile", prof);
}

export function syncIdentityAndProfile({ userId, fullName, zodiacSign, photoPath, gender }) {
  try {
    const profileSnapshot = pickDefined({ fullName, zodiacSign, photoPath, gender });
    try {
      sessionStorage.setItem("profileSnapshot", JSON.stringify(profileSnapshot));
    } catch {}

    // Profile synchronization is deliberately not an auth boundary. It must
    // never reverse an explicit logout by enabling transport or restoring a
    // tab identity from an old async callback.
    setSocketProfile({ fullName, zodiacSign, photoPath, gender });
  } catch {}
}

/* ===== Salas por signo ===== */
export async function joinRoom(payload) {
  const safe = typeof payload === "string" ? { room: payload } : (payload || {});
  const room = canonicalZodiacRoomId(safe.room);
  if (!room) return;

  // Asegurarnos de tener snapshot por pestaña ANTES de intentar unir (arregla carrera en móviles)
  latchUserIdFromLocalStorage();
  try { syncSocketAuth(); } catch {}
  if (!socket.connected) connectWithCurrentAuth();

  // Construir perfil a enviar (sirve incluso si no tenemos userId)
  const prof = {
    ...getLocalProfile(),
    ...pickDefined({
      fullName: safe.fullName,
      zodiacSign: safe.zodiacSign,
      photoPath: safe.photoPath,
      gender: (typeof safe.gender !== "undefined") ? safe.gender : undefined,
    }),
  };

  // Intentar obtener/derivar userId estable para esta pestaña
  let uid = String(safe.userId || getLocalUserId() || "");
  if (!uid) {
    const adopted = latchUserIdFromLocalStorage();
    if (adopted) {
      uid = String(adopted);
      try { sessionStorage.setItem("userIdSnapshot", uid); } catch {}
    }
  }

  const addedDesiredRoom = !joinedRooms.has(room);
  joinedRooms.add(room);
  if (addedDesiredRoom) desiredSubscriptionVersion += 1;

  // If a join/force is in-flight or very recent, avoid duplicating
  const now = Date.now();
  const last = lastJoinAt.get(room) || 0;
  if (joinInflight.has(room) || (now - last < 1000)) {
    // ya hay un join/force reciente; programa un refresh único (900ms)
    scheduleRoomRefresh(room, 900);
    return;
  }

  if (uid) {
    // Camino con identidad fuerte
    const task = (async () => {
      try { await ensureIdentify(uid); } catch {}
      try { await emitAck("setProfile", pickDefined(prof)); } catch {}
      let res;
      try { res = await emitAck("joinRoom", { room }); } catch {}
      lastJoinAt.set(room, Date.now());
      if (res && res.users) {
        dispatchRoomUsers({ room: canonicalZodiacRoomId(res.room) || room, users: res.users, snapshotAt: res.snapshotAt });
      } else {
        scheduleRoomRefresh(room, 400);
      }
    })().finally(() => {
      joinInflight.delete(room);
    });
    joinInflight.set(room, task);
    await task;
  } else {
    // Sin userId (carrera de login): entramos con perfil básico para que figure conectado,
    // y el auto-repair + identify posterior arreglarán el estado.
    console.warn("[socket] joinRoom sin userId — usando perfil básico.");
    try { await emitAck("setProfile", pickDefined(prof)); } catch {}
    let res;
    try { res = await emitAck("joinRoom", { ...safe, ...pickDefined(prof) }); } catch {}
    lastJoinAt.set(room, Date.now());
    if (res && res.users) {
      dispatchRoomUsers({ room: canonicalZodiacRoomId(res.room) || room, users: res.users, snapshotAt: res.snapshotAt });
    } else {
      scheduleRoomRefresh(room, 400);
    }
  }
}
export function leaveRoom(room) {
  const canonicalRoom = canonicalZodiacRoomId(room);
  if (canonicalRoom && joinedRooms.delete(canonicalRoom)) desiredSubscriptionVersion += 1;
  socket.emit("leaveRoom", canonicalRoom || room);
}
export async function sendMessage({ room, text, fullName, photoPath, zodiacSign, gender, userId }) {
  let uid = String(userId || getLocalUserId() || "");
  if (!uid) {
    const adopted = latchUserIdFromLocalStorage();
    if (adopted) {
      uid = String(adopted);
      try { sessionStorage.setItem('userIdSnapshot', uid); } catch {}
    }
  }
  if (!uid) { console.warn('[socket] sendMessage cancelado: no userId.'); return; }

  const base = getLocalProfile();
  const prof = {
    ...base,
    fullName: typeof fullName !== "undefined" ? fullName : base.fullName,
    photoPath: typeof photoPath !== "undefined" ? photoPath : base.photoPath,
    zodiacSign: typeof zodiacSign !== "undefined" ? zodiacSign : base.zodiacSign,
    gender: (typeof gender !== "undefined") ? gender : base.gender,
  };

  try { await ensureIdentify(uid); } catch {}
  try { await emitAck("setProfile", pickDefined(prof)); } catch {}

  socket.emit("chatMessage", {
    room: canonicalZodiacRoomId(room) || room,
    text,
    fullName: prof.fullName,
    photoPath: prof.photoPath,
    zodiacSign: prof.zodiacSign,
    gender: prof.gender,
  });
}

// Private roulette messages use a dedicated, authenticated event.  They never
// send a client-owned identity/profile and cannot enter public room handlers.
export async function sendRouletteMessage({ room, text } = {}) {
  const safeRoom = String(room || '').trim();
  const safeText = typeof text === 'string' ? text.trim() : '';
  logSocketDiagnostic('rouletteMessage', {
    socketConnected: Boolean(socket?.connected),
    socketId: socket?.id || null,
    room: safeRoom || null,
    textLength: safeText.length,
  });
  if (!safeRoom || !safeText || !socket?.connected) {
    const result = { ok: false, error: 'match_room_not_joined' };
    logSocketDiagnostic('rouletteMessage:ack', result);
    return result;
  }
  try {
    const result = await emitAck('rouletteMessage', { room: safeRoom, text: safeText });
    const normalized = result && typeof result === 'object' ? result : { ok: false, error: 'message_rejected' };
    logSocketDiagnostic('rouletteMessage:ack', { ok: Boolean(normalized.ok), error: normalized.error || null, id: normalized.id || null });
    return normalized;
  } catch {
    const result = { ok: false, error: 'message_unavailable' };
    logSocketDiagnostic('rouletteMessage:ack', result);
    return result;
  }
}
export function getRoomUsers(room) {
  const canonicalRoom = canonicalZodiacRoomId(room);
  socket.emit("getRoomUsers", canonicalRoom || room);
}
export function toggleLike({ room, messageId, userKey }) {
  socket.emit("toggleLike", { room, messageId, userKey });
}


/* ===== Ruletas ===== */
export function cancelRoulette() { socket.emit("cancelRoulette"); }
function emitRouletteJoin(event, profile) {
  const payload = { ...(profile || {}), ...getLocalProfile() };
  let authChanged = false;
  try { authChanged = syncSocketAuth(); } catch {}

  const emit = () => {
    try { socket.emit(event, payload); } catch {}
  };
  if (socket.connected && !authChanged) {
    emit();
    return;
  }

  const once = () => {
    try { socket.off('connect', once); } catch {}
    emit();
  };
  try { socket.once('connect', once); } catch {}
  if (socket.connected && authChanged) reconnectWithCurrentAuth('roulette-token-change');
  else connectWithCurrentAuth();
}
export function joinRoulette(profile) {
  let userId = getLocalUserId();
  if (!userId) {
    const adopted = latchUserIdFromLocalStorage();
    if (adopted) {
      userId = String(adopted);
      try { sessionStorage.setItem('userIdSnapshot', userId); } catch {}
    }
  }
  emitRouletteJoin('joinRoulette', profile);
}
export function onRoulettePartner(cb) {
  if (typeof cb !== 'function') return () => {};
  socket.on("roulettePartner", cb);
  return () => {
    try { socket.off("roulettePartner", cb); } catch {}
  };
}
export function onWaitingPartner(cb) {
  if (typeof cb !== 'function') return () => {};
  socket.on("waitingPartner", cb);
  return () => {
    try { socket.off("waitingPartner", cb); } catch {}
  };
}
export function onRouletteMatchEnded(cb) {
  if (typeof cb !== 'function') return () => {};
  socket.on('rouletteMatchEnded', cb);
  return () => {
    try { socket.off('rouletteMatchEnded', cb); } catch {}
  };
}

export function joinOppositesRoulette(profile) {
  let userId = getLocalUserId();
  if (!userId) {
    const adopted = latchUserIdFromLocalStorage();
    if (adopted) {
      userId = String(adopted);
      try { sessionStorage.setItem('userIdSnapshot', userId); } catch {}
    }
  }
  emitRouletteJoin('joinOppositesRoulette', profile);
}
export function onOppositesRoulettePartner(cb) {
  if (typeof cb !== 'function') return () => {};
  socket.on("oppositesRoulettePartner", cb);
  return () => {
    try { socket.off("oppositesRoulettePartner", cb); } catch {}
  };
}
export function onWaitingOppositesPartner(cb) {
  if (typeof cb !== 'function') return () => {};
  socket.on("waitingOppositesPartner", cb);
  return () => {
    try { socket.off("waitingOppositesPartner", cb); } catch {}
  };
}

export function joinCompatiblesRoulette(profile) {
  let userId = getLocalUserId();
  if (!userId) {
    const adopted = latchUserIdFromLocalStorage();
    if (adopted) {
      userId = String(adopted);
      try { sessionStorage.setItem('userIdSnapshot', userId); } catch {}
    }
  }
  emitRouletteJoin('joinCompatiblesRoulette', profile);
}
export function onCompatiblesRoulettePartner(cb) {
  if (typeof cb !== 'function') return () => {};
  socket.on("compatiblesRoulettePartner", cb);
  return () => {
    try { socket.off("compatiblesRoulettePartner", cb); } catch {}
  };
}
export function onWaitingCompatiblesPartner(cb) {
  if (typeof cb !== 'function') return () => {};
  socket.on("waitingCompatiblesPartner", cb);
  return () => {
    try { socket.off("waitingCompatiblesPartner", cb); } catch {}
  };
}

/* ===== Listeners comunes ===== */
export function onRoomUsers(callback) {
  if (typeof callback === "function") {
    roomUsersCallbacks.add(callback);
  }
  // return an unsubscribe helper
  return () => { try { roomUsersCallbacks.delete(callback); } catch {} };
}
export function onMessage(callback) {
  if (typeof callback === "function") messageCallbacks.add(callback);
  return () => { try { messageCallbacks.delete(callback); } catch {} };
}
// Deduped subscription helpers (we already have a single internal socket listener)
export function onUserJoined(callback) {
  if (typeof callback === "function") userJoinedCallbacks.add(callback);
  return () => { try { userJoinedCallbacks.delete(callback); } catch {} };
}
export function onUserLeft(callback) {
  if (typeof callback === "function") userLeftCallbacks.add(callback);
  return () => { try { userLeftCallbacks.delete(callback); } catch {} };
}
export function onLikesUpdate(callback) {
  if (typeof callback === "function") likesUpdateCallbacks.add(callback);
  return () => { try { likesUpdateCallbacks.delete(callback); } catch {} };
}

// Marcar leído (compat: intenta ambos canales)
const __markReadTimers = new Map();
function markConversationRead(conversationId, userId) {
  if (!conversationId) return;
  const id = String(conversationId);
  
  // Debounce por convId para evitar tormenta de eventos
  if (__markReadTimers.has(id)) { clearTimeout(__markReadTimers.get(id)); }
  const t = setTimeout(() => {
    try { socket.emit('conversations:read', { conversationId: id, userId: userId || getLocalUserId() }); } catch {}
    try { socket.emit('conversation:read', { conversationId: id, userId: userId || getLocalUserId() }); } catch {}
    // Actualización optimista del badge a 0 para esa conversación
    try { dispatchBadgeUpdate({ conversationId: id, unread: 0, at: Date.now() }); } catch {}
    try { clearTimeout(t); } catch {}
    try { __markReadTimers.delete(id); } catch {}
  }, 120);
  __markReadTimers.set(id, t);
}

/* ===== Conversaciones 1 a 1 ===== */
export function joinConversation({ conversationId, userId }) {
  const uid = String(userId || getLocalUserId() || '');
  const nextConversationId = conversationId || null;
  if (String(currentConversationId || '') !== String(nextConversationId || '')) desiredSubscriptionVersion += 1;
  currentConversationId = nextConversationId;
  socket.emit('joinConversation', { conversationId, userId: uid });
  // Al entrar, marcar como leído optimistamente (backend hará el ajuste final)
  if (conversationId) markConversationRead(conversationId, uid);
}
export function leaveConversation({ conversationId, userId }) {
  const uid = String(userId || getLocalUserId() || '');
  try { stopTyping(conversationId); } catch {}
  socket.emit('leaveConversation', { conversationId, userId: uid });
  if (currentConversationId && conversationId && String(currentConversationId) === String(conversationId)) {
    currentConversationId = null;
    desiredSubscriptionVersion += 1;
  }
}
export function onNewConversationMessage(cb) {
  if (typeof cb === 'function') newConvMsgCallbacks.add(cb);
  return () => { try { newConvMsgCallbacks.delete(cb); } catch {} };
}

export function onForumNotification(cb) {
  if (typeof cb === 'function') forumNotifCallbacks.add(cb);
  return () => { try { forumNotifCallbacks.delete(cb); } catch {} };
}

export function onUnifiedNotification(cb) {
  if (typeof cb === 'function') unifiedNotifCallbacks.add(cb);
  return () => { try { unifiedNotifCallbacks.delete(cb); } catch {} };
}


// Presence
export function onConversationPresence(cb) {
  if (typeof cb === 'function') conversationPresenceCallbacks.add(cb);
  return () => { try { conversationPresenceCallbacks.delete(cb); } catch {} };
}
export function onConversationPresenceSnapshot(cb) {
  if (typeof cb === 'function') conversationPresenceSnapshotCallbacks.add(cb);
  return () => { try { conversationPresenceSnapshotCallbacks.delete(cb); } catch {} };
}

/** ===== Contactos (solicitudes / altas / bajas) ===== */
export function onContactRequestReceived(cb) {
  if (typeof cb === 'function') contactRequestReceivedCallbacks.add(cb);
  return () => { try { contactRequestReceivedCallbacks.delete(cb); } catch {} };
}
export function onContactAccepted(cb) {
  if (typeof cb === 'function') contactAcceptedCallbacks.add(cb);
  return () => { try { contactAcceptedCallbacks.delete(cb); } catch {} };
}
export function onContactDeclined(cb) {
  if (typeof cb === 'function') contactDeclinedCallbacks.add(cb);
  return () => { try { contactDeclinedCallbacks.delete(cb); } catch {} };
}
export function onContactRemoved(cb) {
  if (typeof cb === 'function') contactRemovedCallbacks.add(cb);
  return () => { try { contactRemovedCallbacks.delete(cb); } catch {} };
}

/** UI helper: escuchar invalidación de sesión (single-session) */
export function onSessionInvalidated(cb) {
  if (typeof window === 'undefined' || typeof cb !== 'function') return () => {};
  const handler = (ev) => { try { cb(ev.detail || {}); } catch {} };
  window.addEventListener('z10:sessionInvalidated', handler);
  // devolver un unsubscribe
  return () => { try { window.removeEventListener('z10:sessionInvalidated', handler); } catch {} };
}

/** Identify explícito (tras login en ESTA pestaña) */
export function identify(userId) {
  if (!userId) return Promise.resolve(null);
  logSocketDiagnostic('identify:called', {
    requestedUserId: String(userId),
    socketId: socket?.id || socket?.__z10_lastSocketId || null,
    socketConnected: Boolean(socket?.connected),
    authVersion: socketAuthVersion(socket),
  });
  return ensureSocketIdentity({ expectedUserId: String(userId), source: 'identify' }).catch(() => null);
}

/** Empujar snapshot actual al backend */
export function pushProfileSnapshot() {
  try {
    const prof = pickDefined(getLocalProfile());
    if (Object.keys(prof).length) socket.emit("setProfile", prof);
  } catch {}
}

export function setSeekingGender(seekingGender) {
  if (typeof seekingGender === "undefined" || seekingGender === null) return;
  socket.emit("setProfile", { seekingGender });

  // mantener el snapshot de esta pestaña coherente
  try {
    lastProfileSnapshot = { ...lastProfileSnapshot, seekingGender };
    sessionStorage.setItem("profileSnapshot", JSON.stringify(lastProfileSnapshot));
  } catch {}
}

/** Distributed private-conversation typing. Server ownership is derived from
 * the authenticated socket; client payloads contain only conversationId. */
const TYPING_REFRESH_MS = 3000;
const TYPING_IDLE_STOP_MS = 1500;
const TYPING_STALE_DISPLAY_MS = 10000;
const typingRefreshTimers = socket.__z10_typing_refresh_timers || (socket.__z10_typing_refresh_timers = new Map());
const typingIdleTimers = socket.__z10_typing_idle_timers || (socket.__z10_typing_idle_timers = new Map());
const incomingTyping = socket.__z10_incoming_typing || (socket.__z10_incoming_typing = new Map());
const incomingTypingTimers = socket.__z10_incoming_typing_timers || (socket.__z10_incoming_typing_timers = new Map());

function normalizeTypingConversationId(value) {
  const id = String(value || '').trim();
  return id && id.length <= 128 ? id : '';
}
function incomingTypingKey(conversationId, userId) { return `${conversationId}:${userId}`; }
function clearIncomingTyping(conversationId, userId, notify = false) {
  const key = incomingTypingKey(conversationId, userId);
  try { clearTimeout(incomingTypingTimers.get(key)); } catch {}
  incomingTypingTimers.delete(key);
  const existed = incomingTyping.delete(key);
  if (notify && existed) dispatchTypingState({ conversationId, userId, typing: false, updatedAt: Date.now() });
}
function dispatchTypingState(payload) {
  if (!payload?.conversationId || !payload?.userId) return;
  const callbacks = payload.typing ? typingCallbacks : stopTypingCallbacks;
  for (const cb of callbacks) {
    try { cb(payload); } catch {}
  }
}
function handleTypingUpdate(rawPayload) {
  const conversationId = normalizeTypingConversationId(rawPayload?.conversationId);
  const userId = String(rawPayload?.userId || '').trim();
  if (!conversationId || !userId) return;
  const payload = {
    conversationId,
    userId,
    typing: Boolean(rawPayload?.typing),
    updatedAt: Number(rawPayload?.updatedAt || Date.now()),
  };
  if (!payload.typing) {
    clearIncomingTyping(conversationId, userId, false);
    dispatchTypingState(payload);
    return;
  }
  const key = incomingTypingKey(conversationId, userId);
  incomingTyping.set(key, payload.updatedAt);
  try { clearTimeout(incomingTypingTimers.get(key)); } catch {}
  incomingTypingTimers.set(key, setTimeout(() => clearIncomingTyping(conversationId, userId, true), TYPING_STALE_DISPLAY_MS));
  dispatchTypingState(payload);
}
export function startTyping(conversationId) {
  const id = normalizeTypingConversationId(conversationId);
  if (!id || !socket?.connected) return;
  if (!typingRefreshTimers.has(id)) {
    try { socket.emit('typing:start', { conversationId: id }); } catch {}
    typingRefreshTimers.set(id, setInterval(() => {
      try { if (socket?.connected) socket.emit('typing:start', { conversationId: id }); } catch {}
    }, TYPING_REFRESH_MS));
  }
  try { clearTimeout(typingIdleTimers.get(id)); } catch {}
  typingIdleTimers.set(id, setTimeout(() => stopTyping(id), TYPING_IDLE_STOP_MS));
}
export function stopTyping(conversationId, { notifyServer = true } = {}) {
  const id = normalizeTypingConversationId(conversationId);
  if (!id) return;
  const timer = typingRefreshTimers.get(id);
  if (timer) {
    try { clearInterval(timer); } catch {}
    typingRefreshTimers.delete(id);
  }
  try { clearTimeout(typingIdleTimers.get(id)); } catch {}
  typingIdleTimers.delete(id);
  if (notifyServer && socket?.connected) {
    try { socket.emit('typing:stop', { conversationId: id }); } catch {}
  }
}
export function stopAllTyping({ notifyServer = true } = {}) {
  for (const conversationId of [...typingRefreshTimers.keys()]) stopTyping(conversationId, { notifyServer });
  for (const key of [...incomingTyping.keys()]) {
    const [conversationId, userId] = key.split(':');
    clearIncomingTyping(conversationId, userId, false);
  }
}
export function clearTypingConversation(conversationId) {
  const id = normalizeTypingConversationId(conversationId);
  if (!id) return;
  stopTyping(id);
  for (const key of [...incomingTyping.keys()]) {
    if (key.startsWith(`${id}:`)) {
      const userId = key.slice(id.length + 1);
      clearIncomingTyping(id, userId, false);
    }
  }
}
export function requestTypingSnapshot(conversationId) {
  const id = normalizeTypingConversationId(conversationId);
  if (!id || !socket?.connected) return Promise.resolve([]);
  return new Promise((resolve) => {
    try {
      socket.timeout(3000).emit('typing:snapshot', { conversationId: id }, (error, response) => {
        if (error || !response?.ok || !Array.isArray(response.users)) return resolve([]);
        response.users.forEach((state) => handleTypingUpdate({ ...state, typing: true }));
        resolve(response.users);
      });
    } catch { resolve([]); }
  });
}
// Narrow compatibility wrappers for existing callers. userId is ignored.
export function emitTyping({ conversationId }) { startTyping(conversationId); }
export function emitStopTyping({ conversationId }) { stopTyping(conversationId); }
const typingCallbacks = socket.__z10_typing_callbacks || (socket.__z10_typing_callbacks = new Set());
const stopTypingCallbacks = socket.__z10_stop_typing_callbacks || (socket.__z10_stop_typing_callbacks = new Set());

if (!socket.__z10_typing_bound) {
  socket.on('typing:update', handleTypingUpdate);
  // Old servers may still emit these two events during a rolling deploy.
  socket.on('typing', (payload) => handleTypingUpdate({ ...(payload || {}), typing: true }));
  socket.on('stopTyping', (payload) => handleTypingUpdate({ ...(payload || {}), typing: false }));
  socket.__z10_typing_bound = true;
}

export function onTyping(cb) {
  if (typeof cb === 'function') typingCallbacks.add(cb);
  return () => { try { typingCallbacks.delete(cb); } catch {} };
}
export function onStopTyping(cb) {
  if (typeof cb === 'function') stopTypingCallbacks.add(cb);
  return () => { try { stopTypingCallbacks.delete(cb); } catch {} };
}

/* ===== util ===== */
export function offAll() {
  // IMPORTANT:
  // Do NOT unbind the shared socket listeners here. This module now uses
  // fan-out Sets and single internal listeners bound once per socket/tab.
  // Clearing the Sets is enough and avoids breaking other mounted components.
  try { roomUsersCallbacks.clear(); } catch {}
  try { zodiacRoomCountCallbacks.clear(); } catch {}
  try { userJoinedCallbacks.clear(); } catch {}
  try { userLeftCallbacks.clear(); } catch {}
  try { messageCallbacks.clear(); } catch {}
  try { likesUpdateCallbacks.clear(); } catch {}
  try { newConvMsgCallbacks.clear(); } catch {}
  try { conversationPresenceCallbacks.clear(); } catch {}
  try { conversationPresenceSnapshotCallbacks.clear(); } catch {}
  try { contactRequestReceivedCallbacks.clear(); } catch {}
  try { contactAcceptedCallbacks.clear(); } catch {}
  try { contactDeclinedCallbacks.clear(); } catch {}
  try { contactRemovedCallbacks.clear(); } catch {}
  try { forumNotifCallbacks.clear(); } catch {}
  try { unifiedNotifCallbacks.clear(); } catch {}
  try { badgeUpdateCallbacks.clear(); } catch {}
  try { convPreviewCallbacks.clear(); } catch {}
  try { typingCallbacks.clear(); } catch {}
  try { stopTypingCallbacks.clear(); } catch {}
  try { presenceUpdateCallbacks.clear(); } catch {}
  try { userPresenceCallbacks.clear(); } catch {}
}

export function reidentifyFromStorage() {
  logSocketDiagnostic('reidentify:called', {
    socketId: socket?.id || socket?.__z10_lastSocketId || null,
    socketConnected: Boolean(socket?.connected),
    authVersion: socketAuthVersion(socket),
  });
  // Asegura snapshot y auth actualizados antes de reidentificar
  try { latchUserIdFromLocalStorage(); } catch {}
  try { syncSocketAuth(); } catch {}
  try { bindPresenceDomEvents(); } catch {}

  const userId = getLocalUserId();
  logSocketDiagnostic('reidentify:storage_resolved', {
    resolvedUserId: userId || null,
    tokenPresent: Boolean(getAuthToken()),
    socketId: socket?.id || socket?.__z10_lastSocketId || null,
    socketConnected: Boolean(socket?.connected),
    authVersion: socketAuthVersion(socket),
  });
  if (!userId) {
    try { joinedRooms.forEach((room) => socket.emit("leaveRoom", room)); } catch {}
    return Promise.resolve(null);
  }
  // Compatibility only. New consumers must await ensureSocketIdentity directly.
  return ensureSocketIdentity({ expectedUserId: String(userId), source: 'reidentifyFromStorage' })
    .then((identity) => { try { startPresenceHeartbeat(); } catch {}; return identity; });
}

export function clearCachedIdentity() {
  lastProfileSnapshot = {};

  try {
    if (getLocalUserId()) socket.emit('presence:bye');
  } catch {}

  // No forzar `identify: null` aquí; otras vistas de esta pestaña pueden seguir montadas.
  try { joinedRooms.clear(); } catch {}

  try {
    sessionStorage.removeItem("userIdSnapshot");
    sessionStorage.removeItem("profileSnapshot");
  } catch {}

  // NO socket.disconnect()
}

/** Presence (conteos por room) */
export function getPresenceCounts(roomIds) {
  const arr = Array.isArray(roomIds) ? roomIds.map(String) : [];
  return new Promise((resolve) => {
    try {
      socket.timeout(3000).emit('presence:get', arr, (err, resp) => {
        if (err) return resolve({});
        const counts = (resp && resp.counts) || {};
        resolve(counts);
      });
    } catch {
      resolve({});
    }
  });
}

export function getZodiacRoomCounts(callback) {
  return new Promise((resolve) => {
    const finish = (payload) => {
      const result = payload && typeof payload === 'object' ? payload : { counts: {}, snapshotAt: 0 };
      try { callback?.(result); } catch {}
      resolve(result);
    };
    try {
      socket.timeout(3000).emit('zodiacRooms:getCounts', (err, payload) => {
        if (err) return finish({ counts: {}, snapshotAt: 0 });
        finish(payload);
      });
    } catch {
      finish({ counts: {}, snapshotAt: 0 });
    }
  });
}

export function onZodiacRoomCounts(callback) {
  if (typeof callback === 'function') zodiacRoomCountCallbacks.add(callback);
  return () => { try { zodiacRoomCountCallbacks.delete(callback); } catch {} };
}


export function onPresenceUpdate(handler) {
  if (typeof handler === 'function') presenceUpdateCallbacks.add(handler);
  return () => { try { presenceUpdateCallbacks.delete(handler); } catch {} };
}

/** Presence por usuario (contactos) */
function queryUsersPresence(userIds) {
  const arr = [...new Set(
    (Array.isArray(userIds) ? userIds : [])
      .map((id) => String(id || '').trim())
      .filter(Boolean)
  )];
  if (!arr.length) return Promise.resolve({});
  return new Promise((resolve) => {
    try {
      socket.timeout(3000).emit('presence:users', arr, (err, resp) => {
        if (err) return resolve({});
        const users = (resp && resp.users) || {};
        resolve(users);
      });
    } catch {
      resolve({});
    }
  });
}

function onUserPresence(handler) {
  if (typeof handler === 'function') userPresenceCallbacks.add(handler);
  return () => { try { userPresenceCallbacks.delete(handler); } catch {} };
}

/** Unread/badges helpers for Contacts */
function fetchUnreadForUsers(userIds) {
  const arr = Array.isArray(userIds) ? userIds.map(String) : [];
  return new Promise((resolve) => {
    // Try preferred channel 'conversations:unread' first; fallback to 'badge:get'
    try {
      socket.timeout(3000).emit('conversations:unread', arr, (err, resp) => {
        if (err) return resolve({});
        if (resp && typeof resp === 'object') return resolve(resp);
        // fallback
        try {
          socket.timeout(3000).emit('badge:get', arr, (err2, resp2) => {
            if (err2) return resolve({});
            resolve(resp2 || {});
          });
        } catch {
          resolve({});
        }
      });
    } catch {
      // hard fallback
      try {
        socket.timeout(3000).emit('badge:get', arr, (err2, resp2) => {
          if (err2) return resolve({});
          resolve(resp2 || {});
        });
      } catch {
        resolve({});
      }
    }
  });
}

function onBadgeUpdate(cb) {
  if (typeof cb !== 'function') return () => {};
  badgeUpdateCallbacks.add(cb);
  return () => { try { badgeUpdateCallbacks.delete(cb); } catch {} };
}

function onConversationPreview(cb) {
  if (typeof cb !== 'function') return () => {};
  convPreviewCallbacks.add(cb);
  return () => { try { convPreviewCallbacks.delete(cb); } catch {} };
}

export function setLocalProfileSnapshot(prof = {}) {
  const delta = pickDefined({
    fullName: prof.fullName || prof.name,
    zodiacSign: prof.zodiacSign,
    photoPath: prof.photoPath || prof.photo,
    gender: typeof prof.gender !== "undefined" ? prof.gender : undefined,
  });
  lastProfileSnapshot = { ...lastProfileSnapshot, ...delta };
  try { sessionStorage.setItem("profileSnapshot", JSON.stringify(lastProfileSnapshot)); } catch {}
}

const SocketAPI = {
  socket,
  // salas por signo
  joinRoom, leaveRoom, sendMessage, sendRouletteMessage, getRoomUsers, toggleLike,
  onRoomUsers, onMessage, onUserJoined, onUserLeft, onLikesUpdate,
  // ruleta
  joinRoulette, onRoulettePartner, onWaitingPartner,
  joinOppositesRoulette, onOppositesRoulettePartner, onWaitingOppositesPartner,
  joinCompatiblesRoulette, onCompatiblesRoulettePartner, onWaitingCompatiblesPartner,
  cancelRoulette,
  // 1 a 1
  // 1 a 1
  joinConversation, leaveConversation,
  startTyping, stopTyping, stopAllTyping, clearTypingConversation, requestTypingSnapshot,
  onNewConversationMessage, onForumNotification, onUnifiedNotification, markConversationRead,
  onConversationPresence, onConversationPresenceSnapshot,
  // contactos
  onContactRequestReceived, onContactAccepted, onContactDeclined, onContactRemoved,
  // utils
  setSocketProfile, syncIdentityAndProfile,
  offAll, reidentifyFromStorage, clearCachedIdentity,
  setLocalProfileSnapshot, pushProfileSnapshot, setSeekingGender,
  // presence
  getPresenceCounts, onPresenceUpdate, queryUsersPresence, onUserPresence,
  getZodiacRoomCounts, onZodiacRoomCounts,
  // presence helpers
  startPresenceHeartbeat, setPresenceInterval,
  // badges & previews
  fetchUnreadForUsers, onBadgeUpdate, onConversationPreview,
};

export default SocketAPI;
// explicit named re-exports to satisfy importers
export { queryUsersPresence, onUserPresence, fetchUnreadForUsers, onBadgeUpdate, onConversationPreview, markConversationRead, startPresenceHeartbeat };
